import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../context/ThemeContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FileText } from 'lucide-react';
import axios from 'axios';
import { 
  getCriticalMineralsDashboardData, 
  getAvailableYears,
  getTradeRestrictionsData
} from '../services/criticalMineralsApi';
import { 
  getRecentKabilDevelopments, 
  getKabilSummary,
  fetchKabilDealsForMapCountry,
  buildKabilAgreementFromDeals
} from '../services/kabilApi';
import { 
  getComprehensiveTimeSeriesData,
  getAvailableMinerals,
  getAvailableFlows
} from '../services/timeSeriesApi';
import {
  getComprehensivePricingData,
  getPricingMinerals,
  getPricingYears,
  getPricingKPIs
} from '../services/pricingApi';
import TimeSeriesCharts from '../components/TimeSeriesCharts';
import PricingTimeSeriesCharts from '../components/PricingTimeSeriesCharts';
import worldMapData from '../data/world-countries.json';
import Flag from 'react-flagkit';
import MineralSectorTreeMap from '../components/MineralSectorTreeMap';
import IndiaDomesticReservesMap from '../components/IndiaDomesticReservesMap';

/** UI label for the dependency metric; API still expects "Import Ratio". */
const METRIC_IMPORT_DEPENDENCY = 'Import Dependency';
const toApiMetric = (metric) => (metric === METRIC_IMPORT_DEPENDENCY ? 'Import Ratio' : metric);

const SupplyMarketsPage = () => {
  const { isDarkMode } = useTheme();
  const [selectedMineral, setSelectedMineral] = useState('Lithium');
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedMetric, setSelectedMetric] = useState(METRIC_IMPORT_DEPENDENCY);
  const [selectedTradeRestriction, setSelectedTradeRestriction] = useState('All');
  
  // New state for view toggles
  const [selectedView, setSelectedView] = useState('Map');
  const [mapProjection, setMapProjection] = useState('2D'); // '2D' or '3D'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [useCanvasRendering, setUseCanvasRendering] = useState(false); // Performance optimization
  
  const [mapData, setMapData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryDetail, setCountryDetail] = useState(null);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [overallRisk, setOverallRisk] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mineralChanging, setMineralChanging] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  const [availableYears, setAvailableYears] = useState(['2024', '2023', '2022', '2021', '2020']);
  const [domesticReservesData, setDomesticReservesData] = useState(null); // For Domestic Reserves table data (filtered by mineral)
  const [domesticReservesAllData, setDomesticReservesAllData] = useState(null); // For KPI calculations (all minerals)
  const [actualYear, setActualYear] = useState('2024');
  
  // Time Series Dashboard states
  const [timeSeriesMineral, setTimeSeriesMineral] = useState('Lithium');
  const [timeSeriesFlow, setTimeSeriesFlow] = useState('Import');
  const [timeSeriesYear, setTimeSeriesYear] = useState('2024');
  const [timeSeriesData, setTimeSeriesData] = useState(null);
  const [filteredTimeSeriesData, setFilteredTimeSeriesData] = useState(null);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(true);
  const [availableTimeSeriesMinerals, setAvailableTimeSeriesMinerals] = useState(['Graphite', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements']);
  const [availableTimeSeriesFlows, setAvailableTimeSeriesFlows] = useState(['Import', 'Export']);
  
  // Pricing Time Series Dashboard states
  const [pricingMineral, setPricingMineral] = useState('Lithium');
  const [pricingYear, setPricingYear] = useState('2024');
  const [pricingData, setPricingData] = useState(null);
  const [filteredPricingData, setFilteredPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [availablePricingMinerals, setAvailablePricingMinerals] = useState(['Lithium', 'Cobalt', 'Nickel']);
  const [availablePricingYears, setAvailablePricingYears] = useState(['2025', '2024', '2023', '2022', '2021', '2020']);
  
  // Pricing KPIs states
  const [pricingKPIs, setPricingKPIs] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  
  
  // Trade Policy Tracker states
  const [tradePolicyData, setTradePolicyData] = useState(null);
  const [tradePolicyLoading, setTradePolicyLoading] = useState(false);
  const [tradePolicyError, setTradePolicyError] = useState(null);
  const [availableTradePolicyMinerals, setAvailableTradePolicyMinerals] = useState([]);
  
  const mapRef = useRef();
  const svgRef = useRef();
  const timeSeriesLeftChartRef = useRef();
  const timeSeriesRightChartRef = useRef();
  
  // New refs for 3D globe
  const globeRef = useRef();
  const globeSvgRef = useRef();

  const minerals = ['All', 'Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite'];
  const importRatioMinerals = ['Lithium', 'Cobalt', 'Nickel', 'Rare Earth Elements', 'Graphite'];
  const metrics = [METRIC_IMPORT_DEPENDENCY, 'World Reserves', 'Trade Restrictions'];
  
  // Separate state for Domestic Reserves section
  const [domesticReservesMineral, setDomesticReservesMineral] = useState('Lithium');
  const [domesticReservesView, setDomesticReservesView] = useState('Map');
  const [domesticReservesType, setDomesticReservesType] = useState('Reserves'); // 'Production' or 'Reserves'
  
  // View options
  const viewOptions = ['Table', 'Map'];
  const domesticReservesViewOptions = ['Table', 'Map'];
  const domesticReservesTypeOptions = ['Production', 'Reserves'];

  // Ensure selectedView is always valid
  useEffect(() => {
    if (!viewOptions.includes(selectedView)) {
      setSelectedView('Table');
    }
  }, [selectedView]);

  // Helper function to get alternative country names for better matching
  const getCountryAltNames = (countryName) => {
    const altNamesMap = {
      'United States': ['USA', 'US', 'United States of America'],
      'United Kingdom': ['UK', 'England', 'Britain', 'Great Britain'],
      'China': ['PRC', 'People\'s Republic of China'],
      'Russia': ['Russian Federation', 'RF'],
      'Australia': ['Aus', 'Oz'],
      'Canada': ['Can'],
      'Germany': ['Deutschland'],
      'France': ['French Republic'],
      'Japan': ['JPN', 'Nippon'],
      'India': ['IND', 'Bharat'],
      'Brazil': ['Brasil'],
      'South Africa': ['RSA', 'ZAF'],
      'South Korea': ['Korea, Republic of', 'Korea', 'Republic of Korea'],
      'Democratic Republic of the Congo': ['DRC', 'Congo', 'RDC'],
      'United Arab Emirates': ['UAE', 'Emirates']
    };
    return altNamesMap[countryName] || [];
  };
  
  // Trade restriction types
  const tradeRestrictionTypes = [
    'All',
    'Export Prohibition',
    'Export License Required',
    'Export Quota',
    'Export Tax',
    'Import Prohibition',
    'Import License Required',
    'Import Quota',
    'Import Tax',
    'Technical Barriers',
    'Sanitary Measures',
    'Other'
  ];

  // Helper function to find country data
  const findCountryData = (countries, countryName) => {
    if (!countries || !Array.isArray(countries)) return null;
    return countries.find(country => {
      const countryField = country.name || country.country || country.country_name;
      return countryField && countryField.toLowerCase() === countryName.toLowerCase();
    });
  };

  // Helper function to get PESTLE risk level colors
  const getPestleRiskLevelColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getPestleRiskLevelDotColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-red-400';
      case 'medium': return 'bg-yellow-400';
      case 'low': return 'bg-green-400';
      default: return 'bg-gray-400';
    }
  };

  // Function to close country detail
  const closeCountryDetail = () => {
    setSelectedCountry(null);
    setCountryDetail(null);
  };

  // Smooth transition function for 2D/3D switching
  const handleProjectionChange = (newProjection) => {
    if (newProjection === mapProjection) return;
    
    console.log('Switching projection from', mapProjection, 'to', newProjection);
    setIsTransitioning(true);
    
    // Add a small delay to show transition effect
    setTimeout(() => {
      setMapProjection(newProjection);
      setIsTransitioning(false);
      
      // Force re-render of the appropriate view
      if (newProjection === '3D' && selectedView === 'Map') {
        setTimeout(() => {
          if (globeRef.current && globeSvgRef.current) {
            console.log('Force rendering 3D globe after projection change');
            renderGlobe();
          }
        }, 200);
      }
    }, 150);
  };

  // Function to handle country click
  const handleCountryClick = async (country, countriesData = []) => {
    console.log('=== HANDLE COUNTRY CLICK DEBUG ===');
    console.log('Country:', country);
    console.log('CountriesData:', countriesData);
    console.log('Selected metric:', selectedMetric);
    console.log('=== END HANDLE COUNTRY CLICK DEBUG ===');
    
    setSelectedCountry(country);
    const countryName = country.properties.name || country.properties.NAME || country.properties.ADMIN || 'Unknown';

    const kabilRes = await fetchKabilDealsForMapCountry(country);
    const kabilFromApi = buildKabilAgreementFromDeals(kabilRes.items);
    const defaultKabilAgreement = {
      status: 'No KABIL agreement',
      type: 'No partnership',
      details: 'No active KABIL partnerships identified'
    };
    const resolveKabilAgreement = (embedded) =>
      kabilFromApi || embedded || defaultKabilAgreement;

    try {
      // Use existing mapData to get country information
      if (mapData && mapData.countries) {
        let countryData = findCountryData(mapData.countries, countryName);
        
        // For Trade Restrictions, use countriesData as primary source since it contains trade restrictions data
        if (selectedMetric === 'Trade Restrictions' && countriesData.length > 0) {
          console.log('=== DEBUG TRADE RESTRICTIONS DATA ===');
          console.log('Country name:', countryName);
          console.log('CountriesData length:', countriesData.length);
          console.log('CountriesData:', countriesData);
          
          const tradeRestrictionData = countriesData.find(c => 
            c.name && c.name.toLowerCase() === countryName.toLowerCase()
          );
          
          console.log('Found trade restriction data:', tradeRestrictionData);
          
          if (tradeRestrictionData) {
            // Use trade restriction data as the primary country data
            countryData = tradeRestrictionData;
            console.log('Using trade restriction data as primary country data:', countryData);
          } else {
            console.log('No trade restriction data found for:', countryName);
          }
          console.log('=== END DEBUG TRADE RESTRICTIONS DATA ===');
        }
        
        // Create country detail object
        const getMetricValue = () => {
          if (!countryData) return 'N/A';
          
          switch (selectedMetric) {
            case METRIC_IMPORT_DEPENDENCY:
              return `${countryData.dependencyRatio || 0}%`;
            case 'World Reserves':
              return `${countryData.world_reserves || countryData.worldReserves || 0}%`;
            case 'Domestic Reserves':
              return `${countryData.domestic_reserves || countryData.domesticReserves || 0}%`;
            case 'Trade Restrictions':
              return `${countryData.trade_restrictions || countryData.tradeRestrictions || 0}%`;
            default:
              return `${countryData.dependencyRatio || 0}%`;
          }
        };

        let detail;
        
        if (selectedMetric === 'World Reserves') {
          // Special modal for World Reserves using real database data
          const productionData = countryData?.prod_est_2024 || countryData?.prod_2023 || 'N/A';
          const reserveData = countryData?.reserveValue || 'N/A';
          const dataSource = countryData?.dataSource || 'MCS2025';
          
          detail = {
            countryInfo: {
              name: `${countryName} - ${selectedMineral}`,
              year: '2024',
              productionData: productionData !== 'N/A' ? `${productionData.toLocaleString()} tonnes` : 'N/A',
              reserveData: reserveData !== 'N/A' ? `${reserveData.toLocaleString()} tonnes` : 'N/A',
              dataSource: dataSource
            },
            isWorldReserves: true
          };
        } else if (selectedMetric === 'Trade Restrictions') {
          // Special modal for Trade Restrictions
          const restrictionCount = countryData?.tradeRestrictions || countryData?.trade_restrictions || 0;
          const restrictionTypes = countryData?.restrictionTypes || [];
          const primaryRestriction = countryData?.primary_restriction || 'No Restrictions';
          
          console.log('=== CREATING TRADE RESTRICTIONS MODAL ===');
          console.log('Country data for modal:', countryData);
          console.log('Restriction count:', restrictionCount);
          console.log('Restriction types:', restrictionTypes);
          console.log('=== END CREATING TRADE RESTRICTIONS MODAL ===');
          
          detail = {
            countryInfo: {
              name: countryName,
              indiaFlag: "🇮🇳",
              indiaText: `India's ${selectedMetric.toLowerCase()}`
            },
            dependencyRatio: getMetricValue(),
            countryAlerts: countryData?.alerts || [],
            kabilAgreement: resolveKabilAgreement(countryData?.kabilAgreement),
            isTradeRestrictions: true,
            restrictionCount: restrictionCount,
            restrictionTypes: restrictionTypes,
            primary_restriction: primaryRestriction
          };
        } else {
          // Standard modal for other metrics
          detail = {
            countryInfo: {
              name: countryName,
              indiaFlag: "🇮🇳",
              indiaText: `India's ${selectedMetric.toLowerCase()}`
            },
            dependencyRatio: getMetricValue(),
            countryAlerts: countryData?.alerts || [],
            kabilAgreement: resolveKabilAgreement(countryData?.kabilAgreement)
          };
        }
        
        setCountryDetail(detail);
      } else {
        // Fallback data if no map data available
        let detail;
        
        // For Trade Restrictions, use countriesData as primary source
        let countryData = null;
        if (selectedMetric === 'Trade Restrictions' && countriesData.length > 0) {
          console.log('=== DEBUG FALLBACK TRADE RESTRICTIONS DATA ===');
          console.log('Country name:', countryName);
          console.log('CountriesData length:', countriesData.length);
          console.log('CountriesData:', countriesData);
          
          countryData = countriesData.find(c => 
            c.name && c.name.toLowerCase() === countryName.toLowerCase()
          );
          
          console.log('Found fallback country data:', countryData);
          console.log('=== END DEBUG FALLBACK TRADE RESTRICTIONS DATA ===');
        }
        
        if (selectedMetric === 'World Reserves') {
          // Special modal for World Reserves - try to find data from countries array
          const foundCountry = countriesData.find(c => 
            c.name && c.name.toLowerCase() === countryName.toLowerCase()
          );
          
          const productionData = foundCountry?.prod_est_2024 || foundCountry?.prod_2023 || 'N/A';
          const reserveData = foundCountry?.reserveValue || 'N/A';
          const dataSource = foundCountry?.dataSource || 'MCS2025';
          
          detail = {
            countryInfo: {
              name: `${countryName} - ${selectedMineral}`,
              year: '2024',
              productionData: productionData !== 'N/A' ? `${productionData.toLocaleString()} tonnes` : 'N/A',
              reserveData: reserveData !== 'N/A' ? `${reserveData.toLocaleString()} tonnes` : 'N/A',
              dataSource: dataSource
            },
            isWorldReserves: true
          };
        } else if (selectedMetric === 'Trade Restrictions') {
          // Special modal for Trade Restrictions - fallback
          const restrictionCount = countryData?.tradeRestrictions || countryData?.trade_restrictions || 0;
          const restrictionTypes = countryData?.restrictionTypes || [];
          const primaryRestriction = countryData?.primary_restriction || 'No Restrictions';
          
          detail = {
            countryInfo: {
              name: countryName,
              indiaFlag: "🇮🇳",
              indiaText: `India's ${selectedMetric.toLowerCase()}`
            },
            dependencyRatio: 'N/A',
            countryAlerts: [],
            kabilAgreement: resolveKabilAgreement(),
            isTradeRestrictions: true,
            restrictionCount: restrictionCount,
            restrictionTypes: restrictionTypes,
            primary_restriction: primaryRestriction
          };
        } else {
          // Standard modal for other metrics
          detail = {
            countryInfo: {
              name: countryName,
              indiaFlag: "🇮🇳",
              indiaText: `India's ${selectedMetric.toLowerCase()}`
            },
            dependencyRatio: 'N/A',
            countryAlerts: [],
            kabilAgreement: resolveKabilAgreement()
          };
        }
        setCountryDetail(detail);
      }
    } catch (error) {
      console.error('Error loading country details:', error);
      // Set fallback data on error
      let detail;
      
      if (selectedMetric === 'World Reserves') {
        // Special modal for World Reserves - try to find data from countries array
        const countryName = country.properties.name || 'Unknown';
        const foundCountry = countriesData.find(c => 
          c.name && c.name.toLowerCase() === countryName.toLowerCase()
        );
        
        const productionData = foundCountry?.prod_est_2024 || foundCountry?.prod_2023 || 'N/A';
        const reserveData = foundCountry?.reserveValue || 'N/A';
        const dataSource = foundCountry?.dataSource || 'MCS2025';
        
        detail = {
          countryInfo: {
            name: `${countryName} - ${selectedMineral}`,
            year: '2024',
            productionData: productionData !== 'N/A' ? `${productionData.toLocaleString()} tonnes` : 'N/A',
            reserveData: reserveData !== 'N/A' ? `${reserveData.toLocaleString()} tonnes` : 'N/A',
            dataSource: dataSource
          },
          isWorldReserves: true
        };
      } else if (selectedMetric === 'Trade Restrictions') {
        // Special modal for Trade Restrictions - error fallback
        // Use countriesData as primary source
        const countryName = country.properties.name || 'Unknown';
        const tradeRestrictionData = countriesData.find(c => 
          c.name && c.name.toLowerCase() === countryName.toLowerCase()
        );
        const restrictionCount = tradeRestrictionData?.tradeRestrictions || tradeRestrictionData?.trade_restrictions || 0;
        const restrictionTypes = tradeRestrictionData?.restrictionTypes || [];
        const primaryRestriction = tradeRestrictionData?.primary_restriction || 'No Restrictions';
        
        detail = {
          countryInfo: {
            name: countryName,
            indiaFlag: "🇮🇳",
            indiaText: `India's ${selectedMetric.toLowerCase()}`
          },
          dependencyRatio: 'N/A',
          countryAlerts: [],
          kabilAgreement: resolveKabilAgreement(),
          isTradeRestrictions: true,
          restrictionCount: restrictionCount,
          restrictionTypes: restrictionTypes
        };
      } else {
        // Standard modal for other metrics
        detail = {
          countryInfo: {
            name: country.properties.name || 'Unknown',
            indiaFlag: "🇮🇳",
            indiaText: `India's ${selectedMetric.toLowerCase()}`
          },
          dependencyRatio: 'N/A',
          countryAlerts: [],
          kabilAgreement: resolveKabilAgreement()
        };
      }
      setCountryDetail(detail);
    }
  };

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        console.log('Loading dashboard data...');
        const [dashboardData, yearsData, tradeRestrictionsData] = await Promise.all([
          getCriticalMineralsDashboardData(selectedMineral === 'All' ? null : selectedMineral, selectedYear, toApiMetric(selectedMetric)),
          getAvailableYears(selectedMineral === 'All' ? null : selectedMineral, toApiMetric(selectedMetric)),
          selectedMetric === 'Trade Restrictions' ? getTradeRestrictionsData(selectedMineral === 'All' ? null : selectedMineral, selectedYear, 'All') : getTradeRestrictionsData()
        ]);
        
        console.log('Dashboard data loaded:', dashboardData);
        console.log('Years data loaded:', yearsData);
        console.log('Trade restrictions data loaded:', tradeRestrictionsData);
        
        // Additional debugging for World Reserves
        if (selectedMetric === 'World Reserves') {
          console.log('=== WORLD RESERVES API RESPONSE DEBUG ===');
          console.log('Full API response:', dashboardData);
          console.log('Response type:', typeof dashboardData);
          console.log('Response keys:', Object.keys(dashboardData || {}));
          console.log('Countries in response:', dashboardData?.countries?.length || 0);
          console.log('Countries array exists:', !!dashboardData?.countries);
          console.log('Countries array type:', Array.isArray(dashboardData?.countries));
          
          if (dashboardData?.countries?.length > 0) {
            console.log('First country in API response:', dashboardData.countries[0]);
            console.log('All field names in first country:', Object.keys(dashboardData.countries[0] || {}));
          } else {
            console.log('⚠️ NO COUNTRIES DATA - API might not support World Reserves for this mineral/year');
            console.log('Available data keys:', Object.keys(dashboardData || {}));
          }
          console.log('=== END WORLD RESERVES API RESPONSE DEBUG ===');
        }
        
        if (selectedMetric === 'Trade Restrictions' && tradeRestrictionsData?.success) {
          console.log('Trade restrictions API response:', {
            success: tradeRestrictionsData.success,
            countriesCount: tradeRestrictionsData.data?.countries?.length || 0,
            countries: tradeRestrictionsData.data?.countries || [],
            summary: tradeRestrictionsData.data?.summary
          });
          console.log('API was called with parameters:', {
            mineral: selectedMineral,
            year: selectedYear,
            restriction_type: 'All'
          });
        }
        
        // Debug data for specific mineral and metric combinations
        console.log(`=== DEBUG FOR ${selectedMineral} - ${selectedMetric} ===`);
        console.log('API Request params:', { selectedMineral, selectedYear, selectedMetric });
        console.log('Dashboard data structure:', dashboardData);
        console.log('Countries array:', dashboardData?.countries);
        console.log('Countries count:', dashboardData?.countries?.length);
        
        // Check specific World Reserves data
        if (selectedMetric === 'World Reserves' && dashboardData?.countries) {
          console.log('=== WORLD RESERVES DATA ANALYSIS ===');
          const sampleCountry = dashboardData.countries[0];
          console.log('Sample country data:', sampleCountry);
          console.log('Available fields in sample country:', Object.keys(sampleCountry || {}));
          
          // Check for reserve-related fields
          const reserveFields = ['reserveValue', 'worldReserves', 'world_reserves', 'reserves', 'reserve_2024', 'reserve_2023', 'production_2024', 'prod_est_2024'];
          reserveFields.forEach(field => {
            if (sampleCountry && sampleCountry[field] !== undefined) {
              console.log(`Field '${field}':`, sampleCountry[field]);
            }
          });
          console.log('=== END WORLD RESERVES DATA ANALYSIS ===');
        }
        
        if (dashboardData?.countries?.length > 0) {
          console.log('First country sample:', dashboardData.countries[0]);
          console.log('Available fields in first country:', Object.keys(dashboardData.countries[0]));
        } else {
          console.log('⚠️ NO COUNTRIES DATA RETURNED FROM API');
          console.log('Full dashboard data:', JSON.stringify(dashboardData, null, 2));
        }
        console.log(`=== END DEBUG FOR ${selectedMineral} - ${selectedMetric} ===`);
        
        // Use trade restrictions data as primary source when Trade Restrictions metric is selected
        let finalMapData = dashboardData;
        if (selectedMetric === 'Trade Restrictions' && tradeRestrictionsData?.success && tradeRestrictionsData?.data?.countries) {
          // Use trade restrictions data as the primary source
          const tradeRestrictionsCountries = tradeRestrictionsData.data.countries;
          
          // Transform trade restrictions data to match expected format
          const transformedCountries = tradeRestrictionsCountries.map(tr => ({
            name: tr.country,
            country: tr.country,
            country_code: tr.country_code,
            tradeRestrictions: tr.restriction_count || 0,
            trade_restrictions: tr.restriction_count || 0,
            restrictionTypes: [{ type: tr.primary_restriction }],
            primary_restriction: tr.primary_restriction,
            color: tr.color,
            mineral: tr.mineral,
            year: tr.year,
            // Add default values for other fields
            dependencyRatio: 0,
            worldReserves: 0,
            domesticReserves: 0,
            partnerRisk: 0,
            iso3: tr.country_code,
            altNames: getCountryAltNames(tr.country)
          }));
          
          // Create new map data with trade restrictions countries
          finalMapData = {
            ...dashboardData,
            countries: transformedCountries,
            dataSource: tradeRestrictionsData.data.summary?.dataSource || "Trade Restrictions Database"
          };
          
          console.log('Using trade restrictions data as primary source:', transformedCountries.length, 'countries');
        }
        
        setMapData(finalMapData);
        
        // For Import Ratio, ensure 2024 is included and sorted first
        let yearsToSet = yearsData?.years || ['2024', '2023', '2022', '2021', '2020'];
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          // Ensure 2024 is included
          if (!yearsToSet.includes('2024')) {
            yearsToSet = ['2024', ...yearsToSet];
          }
          // Sort years in descending order with 2024 first
          yearsToSet = [...new Set(yearsToSet)].sort((a, b) => {
            // Put 2024 first, then sort others descending
            if (a === '2024') return -1;
            if (b === '2024') return 1;
            return parseInt(b) - parseInt(a);
          });
        }
        
        setAvailableYears(yearsToSet);
        setLiveAlerts(finalMapData?.alerts || []);
        setOverallRisk(finalMapData?.overallRisk || 0);
        setActualYear(yearsData?.latest_year || '2024');
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        console.log('=== API ERROR DEBUG ===');
        console.log('Error details:', error);
        console.log('Error response:', error.response?.data);
        console.log('Error status:', error.response?.status);
        console.log('Request params:', { selectedMineral, selectedYear, selectedMetric });
        console.log('=== END API ERROR DEBUG ===');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [selectedMineral, selectedYear, selectedMetric]);

  // Keep year aligned with metric defaults:
  // - Import Ratio prefers 2024
  // - Trade Restrictions prefers 2023
  useEffect(() => {
    if (selectedMetric === METRIC_IMPORT_DEPENDENCY && availableYears.length > 0) {
      // If 2024 is available, use it; otherwise use the first available year
      if (availableYears.includes('2024')) {
        if (selectedYear !== '2024') {
          setSelectedYear('2024');
        }
      } else if (!availableYears.includes(selectedYear)) {
        // If selectedYear is not in availableYears, use the first available year
        setSelectedYear(availableYears[0]);
      }
      return;
    }

    if (selectedMetric === 'Trade Restrictions' && availableYears.length > 0) {
      if (availableYears.includes('2023')) {
        if (selectedYear !== '2023') {
          setSelectedYear('2023');
        }
      } else if (!availableYears.includes(selectedYear)) {
        setSelectedYear(availableYears[0]);
      }
    }
  }, [selectedMetric, availableYears, selectedYear]);

  // Load time series data
  useEffect(() => {
    const loadTimeSeriesData = async () => {
      try {
        setTimeSeriesLoading(true);
        const data = await getComprehensiveTimeSeriesData(timeSeriesMineral, timeSeriesFlow, timeSeriesYear);
        setTimeSeriesData(data);
        setFilteredTimeSeriesData(data);
      } catch (error) {
        console.error('Error loading time series data:', error);
      } finally {
        setTimeSeriesLoading(false);
      }
    };

    loadTimeSeriesData();
  }, [timeSeriesMineral, timeSeriesFlow, timeSeriesYear]);

  // Load pricing data
  useEffect(() => {
    const loadPricingData = async () => {
      try {
        setPricingLoading(true);
        const data = await getComprehensivePricingData(pricingMineral, pricingYear);
        setPricingData(data);
        setFilteredPricingData(data);
      } catch (error) {
        console.error('Error loading pricing data:', error);
      } finally {
        setPricingLoading(false);
      }
    };

    loadPricingData();
  }, [pricingMineral, pricingYear]);

  // Load pricing KPIs
  useEffect(() => {
    const loadPricingKPIs = async () => {
      try {
        setKpiLoading(true);
        const data = await getPricingKPIs();
        setPricingKPIs(data);
      } catch (error) {
        console.error('Error loading pricing KPIs:', error);
      } finally {
        setKpiLoading(false);
      }
    };
    loadPricingKPIs();
  }, []);


  // Load available pricing years
  useEffect(() => {
    const loadPricingYears = async () => {
      try {
        const data = await getPricingYears();
        if (data.years) {
          setAvailablePricingYears(['All', ...data.years]);
        }
      } catch (error) {
        console.error('Error loading pricing years:', error);
      }
    };
    loadPricingYears();
  }, []);

  // 3D Globe Rendering
  const renderGlobe = useCallback(() => {
    console.log('renderGlobe called, globeRef:', globeRef.current, 'globeSvgRef:', globeSvgRef.current, 'mapData:', mapData);
    
    if (!globeRef.current || !globeSvgRef.current) {
      console.log('Missing required refs for globe:', { globeRef: !!globeRef.current, globeSvgRef: !!globeSvgRef.current });
      return;
    }

    // Check if we're still in 3D mode
    if (mapProjection !== '3D') {
      console.log('Not in 3D mode, skipping globe render. Current projection:', mapProjection);
      return;
    }

    console.log('Rendering 3D globe with projection:', mapProjection);

    try {
      const width = globeRef.current.clientWidth || 800;
      const height = globeRef.current.clientHeight || 600;
      
      console.log('Globe dimensions:', { width, height });

      // Clear previous globe and tooltips
      d3.select(globeSvgRef.current).selectAll("*").remove();
      d3.selectAll('.globe-tooltip').remove();
      
      // Remove any existing drag listeners
      d3.select(globeSvgRef.current).on('.drag', null);

      const svg = d3.select(globeSvgRef.current)
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background-color', '#ffffff');

      // Create tooltip
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'globe-tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '8px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');

      // Create 3D projection with smooth transitions
      const projection = d3.geoOrthographic()
        .scale(150)
        .translate([width / 2, height / 2])
        .rotate([0, 0, 0]);

      const path = d3.geoPath().projection(projection);

      // Add smooth transition for initial render
      svg.style('opacity', 0)
        .transition()
        .duration(300)
        .style('opacity', 1);

      // Handle different possible data structures from API
      let countriesData = [];
      if (mapData) {
        if (Array.isArray(mapData.countries)) {
          countriesData = mapData.countries;
        } else if (Array.isArray(mapData.data)) {
          countriesData = mapData.data;
        } else if (Array.isArray(mapData)) {
          countriesData = mapData;
        }
      }
      
      if (countriesData.length === 0) {
        console.log('No API countries data available for globe - showing empty globe');
        countriesData = [];
      }
      
      // Transform API data to match expected format
      if (mapData && mapData.countries && mapData.countries.length > 0) {
        countriesData = mapData.countries.map(country => ({
          country: country.name,
          dependency_ratio: country.dependencyRatio || 0,
          world_reserves: country.worldReserves || 0,
          domestic_reserves: country.domesticReserves || 0,
          trade_restrictions: country.tradeRestrictions || country.trade_restrictions || 0,
          restrictionTypes: country.restrictionTypes || [],
          primary_restriction: country.primary_restriction || 'No Restrictions',
          color: country.color || '#059669',
          partner_risk: country.partnerRisk || 0,
          iso3: country.iso3,
          tradeData: country.tradeData,
          name: country.name,
          altNames: getCountryAltNames(country.name),
          reserveValue: country.reserveValue || country.worldReserves || 0,
          prod_est_2024: country.prod_est_2024 || null,
          prod_2023: country.prod_2023 || null,
          dataSource: country.dataSource
        }));
      }

      // Color scale based on selected metric
      const getColorScale = () => {
        const metricMap = {
          [METRIC_IMPORT_DEPENDENCY]: 'dependency_ratio',
          'World Reserves': 'world_reserves', 
          'Domestic Reserves': 'domestic_reserves',
          'Trade Restrictions': 'trade_restrictions',
          'Partner Risk': 'partner_risk'
        };
        
        const metricKey = metricMap[selectedMetric] || 'dependency_ratio';
        
        // For Import Ratio, use all values including 0 for proper scale calculation
        let values;
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          values = countriesData
            .map(d => d[metricKey])
            .filter(v => v !== null && v !== undefined && v !== 'N/A');
        } else {
          values = countriesData.map(d => d[metricKey] || 0);
        }
        
        const maxValue = values.length > 0 ? Math.max(...values) : 1;
        
        let colorScale;
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          // Use fixed percentage domain to match the legend (0-100%)
          // Adjusted to make low values more visible with better contrast
          colorScale = d3.scaleLinear()
            .domain([0, 5, 15, 30, 50, 100])
            .range(['#F0F9E8', '#CCEBC5', '#A8DDB5', '#7BCCC4', '#43A2CA', '#0868AC']);
        } else if (selectedMetric === 'Trade Restrictions') {
          colorScale = d3.scaleLinear()
            .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
            .range(['#e5e7eb', '#fef3c7', '#fbbf24', '#f59e0b', '#dc2626']);
        } else {
          colorScale = d3.scaleLinear()
            .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
            .range(['#e5e7eb', '#86efac', '#fde047', '#60a5fa', '#fbbf24']);
        }
        
        return colorScale;
      };

      const colorScale = getColorScale();

      // Draw countries on globe
      const countries = svg.selectAll('.country')
        .data(worldMapData.features)
        .enter()
        .append('path')
        .attr('class', 'country')
        .attr('d', path)
        .attr('fill', d => {
          if (selectedMetric === 'World Reserves') {
            return '#ffffff'; // White for all countries in World Reserves
          }
          
          if (selectedMetric === 'Trade Restrictions') {
            return '#ffffff'; // White for all countries in Trade Restrictions
          }
          
          const countryName = d.properties.name || d.properties.NAME;
          const countryData = countriesData.find(c => {
            if (!c || !countryName) return false;
            const countryField = c.country || c.name || c.country_name || c.countryName;
            if (!countryField) return false;
            
            if (countryField.toLowerCase() === countryName.toLowerCase()) {
              return true;
            }
            
            if (c.altNames && Array.isArray(c.altNames)) {
              return c.altNames.some(altName => 
                altName.toLowerCase() === countryName.toLowerCase()
              );
            }
            
            return false;
          });
          
          if (countryData) {
            const metricMap = {
              [METRIC_IMPORT_DEPENDENCY]: 'dependency_ratio',
              'World Reserves': 'world_reserves', 
              'Domestic Reserves': 'domestic_reserves',
              'Trade Restrictions': 'trade_restrictions',
              'Partner Risk': 'partner_risk'
            };
            
            const metricKey = metricMap[selectedMetric] || 'dependency_ratio';
            const value = countryData[metricKey];
            
            // For Import Ratio, return white for NA/null/undefined or 0 values
            if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
              if (value === null || value === undefined || value === 'N/A' || value === 0) {
                return '#ffffff';
              }
            }
            
            const actualValue = value || 0;
            return colorScale(actualValue);
          }
          
          // For countries without data, return white for Import Ratio, otherwise use color scale with 0 value
          if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
            return '#ffffff';
          }
          return colorScale(0);
        })
        .attr('stroke', '#4b5563')
        .attr('stroke-width', 0.5)
        .style('cursor', 'grab')
        .on('mouseover', function(event, d) {
          d3.select(this)
            .transition()
            .duration(100)
            .attr('stroke', '#374151')
            .attr('stroke-width', 2);
        })
        .on('mouseout', function() {
          d3.select(this)
            .transition()
            .duration(100)
            .attr('stroke', '#4b5563')
            .attr('stroke-width', 0.5);
        })
        .on('click', function(event, d) {
          d.clickX = event.clientX;
          d.clickY = event.clientY;
          
          if (globeRef.current) {
            const globeRect = globeRef.current.getBoundingClientRect();
            d.mapOffsetX = globeRect.left;
            d.mapOffsetY = globeRect.top;
            d.clickXRelative = event.clientX - globeRect.left;
            d.clickYRelative = event.clientY - globeRect.top;
          }
          
          handleCountryClick(d, countriesData);
        });

      // Add graticule for 3D effect
      const graticule = d3.geoGraticule();
      svg.append('path')
        .datum(graticule)
        .attr('class', 'graticule')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', '#4b5563')
        .attr('stroke-width', 0.5)
        .attr('opacity', 0.3);

      // Add circular overlays for World Reserves metric
      if (selectedMetric === 'World Reserves') {
        const reserveData = countriesData.filter(d => {
          const hasReserves = (d.reserveValue && d.reserveValue > 0) ||
                             (d.world_reserves && d.world_reserves > 0) ||
                             (d.reserve_2024 && d.reserve_2024 > 0);
          return hasReserves;
        });
        
        const circles = svg.selectAll('.reserve-circle')
          .data(reserveData)
          .enter()
          .append('circle')
          .attr('class', 'reserve-circle')
          .filter(d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              return countryName && d.name && 
                countryName.toLowerCase() === d.name.toLowerCase();
            });
            return countryFeature !== undefined;
          })
          .attr('cx', d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return x;
            }
            return null;
          })
          .attr('cy', d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return y;
            }
            return null;
          })
          .attr('r', d => {
            const value = d.reserveValue || 0;
            const minRadius = 8;
            const maxRadius = 60;
            const maxValue = Math.max(...reserveData.map(c => c.reserveValue || 0));
            if (maxValue === 0) return minRadius;
            return minRadius + (value / maxValue) * (maxRadius - minRadius);
          })
          .attr('fill', '#254b8c')
          .attr('fill-opacity', 0.7)
          .attr('stroke', '#1e40af')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.8)
          .style('cursor', 'pointer')
          .on('mouseover', function(event, d) {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3)
              .attr('stroke-opacity', 1);
              
            tooltip
              .style('opacity', 1)
              .html(`
                <div class="bg-gray-800 text-white p-2 rounded shadow-lg">
                  <div class="font-semibold">${d.name}</div>
                  <div class="text-sm">Reserves: ${d.reserveValue.toLocaleString()} tonnes</div>
                  <div class="text-sm">Production: ${(d.prod_est_2024 || d.prod_2023 || 'N/A')} tonnes</div>
                  <div class="text-xs text-gray-300">Source: ${d.dataSource || 'MCS2025'}</div>
                </div>
              `)
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 1.5)
              .attr('stroke-opacity', 0.8);
              
            tooltip.style('opacity', 0);
          })
          .on('click', function(event, d) {
            const mockCountry = {
              properties: { name: d.name },
              clickX: event.clientX,
              clickY: event.clientY,
              clickXRelative: event.clientX - (globeRef.current?.getBoundingClientRect().left || 0),
              clickYRelative: event.clientY - (globeRef.current?.getBoundingClientRect().top || 0)
            };
            handleCountryClick(mockCountry, reserveData);
          });
      }
      
      // Add circular overlays for Trade Restrictions metric
      if (selectedMetric === 'Trade Restrictions') {
        const tradeRestrictionData = countriesData;
        
        const restrictionTypeColors = {
          'Export Prohibition': '#dc2626',
          'Export Quota': '#f59e0b',
          'Export Tax': '#fbbf24',
          'Export License Required': '#10b981',
          'Import Prohibition': '#dc2626',
          'Import Quota': '#f59e0b',
          'Import Tax': '#fbbf24',
          'Import License Required': '#10b981',
          'Technical Barriers': '#ec4899',
          'Sanitary Measures': '#ec4899',
          'Other': '#ec4899',
          'No Restrictions': '#059669',
          'Not Researched': '#6b7280'
        };
        
        const circles = svg.selectAll('.trade-restriction-circle')
          .data(tradeRestrictionData)
          .enter()
          .append('circle')
          .attr('class', 'trade-restriction-circle')
          .filter(d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              return countryName && d.name && 
                countryName.toLowerCase() === d.name.toLowerCase();
            });
            return countryFeature !== undefined;
          })
          .attr('cx', d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return x;
            }
            return null;
          })
          .attr('cy', d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return y;
            }
            return null;
          })
          .attr('r', 8)
          .attr('fill', d => {
            if (d.color) {
              return d.color;
            }
            
            if (d.primary_restriction) {
              return restrictionTypeColors[d.primary_restriction] || restrictionTypeColors['Other'];
            }
            
            if (d.restrictionTypes && d.restrictionTypes.length > 0) {
              const firstType = d.restrictionTypes[0].type || d.restrictionTypes[0];
              return restrictionTypeColors[firstType] || restrictionTypeColors['Other'];
            }
            
            return restrictionTypeColors['Not Researched'];
          })
          .attr('fill-opacity', 0.8)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.9)
          .style('cursor', 'pointer')
          .on('mouseover', function(event, d) {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3)
              .attr('stroke-opacity', 1);
              
            const restrictionTypes = d.restrictionTypes || [];
            const restrictionCount = d.trade_restrictions || d.tradeRestrictions || 0;
            const primaryRestriction = d.primary_restriction || 'No Restrictions';
            
            tooltip
              .style('opacity', 1)
              .html(`
                <div class="bg-gray-800 text-white p-2 rounded shadow-lg">
                  <div class="font-semibold">${d.name}</div>
                  <div class="text-sm">Restriction: ${primaryRestriction}</div>
                  ${restrictionTypes.length > 0 ? `
                    <div class="text-sm">Types: ${restrictionTypes.map(rt => rt.type || rt).join(', ')}</div>
                  ` : ''}
                </div>
              `)
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 1.5)
              .attr('stroke-opacity', 0.9);
              
            tooltip.style('opacity', 0);
          })
          .on('click', function(event, d) {
            const mockCountry = {
              properties: { name: d.name },
              clickX: event.clientX,
              clickY: event.clientY,
              clickXRelative: event.clientX - (globeRef.current?.getBoundingClientRect().left || 0),
              clickYRelative: event.clientY - (globeRef.current?.getBoundingClientRect().top || 0)
            };
            handleCountryClick(mockCountry, tradeRestrictionData);
          });
      }

      // Zoom with faster wheel / trackpad-pinch sensitivity
      const zoom = d3.zoom()
        .scaleExtent([0.5, 5])
        .wheelDelta((event) => -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.012))
        .filter((event) => {
          if (event.type === 'wheel') return true;
          if (event.type === 'dblclick') return false;
          return !event.button;
        })
        .on('zoom', (event) => {
          if (!globeRef.current || mapProjection !== '3D') return;
          const newScale = 150 * event.transform.k;
          projection.scale(newScale);
          redrawGlobe();
        });

      // Globe drag rotation using incremental dx/dy from D3 drag events
      const dragSensitivity = 0.4;
      let lastDx = 0;
      let lastDy = 0;

      const redrawGlobe = () => {
        countries.attr('d', path);
        svg.select('.graticule').attr('d', path);
        svg.selectAll('.reserve-circle, .trade-restriction-circle').each(function(d) {
          const countryFeature = worldMapData.features.find(f => {
            const countryName = f.properties.name || f.properties.NAME;
            return countryName && d.name &&
              countryName.toLowerCase() === d.name.toLowerCase();
          });
          if (countryFeature) {
            const centroid = d3.geoCentroid(countryFeature);
            const [cx, cy] = projection(centroid);
            d3.select(this).attr('cx', cx).attr('cy', cy);
          }
        });
      };

      const drag = d3.drag()
        .on('start', function() {
          if (!globeRef.current || mapProjection !== '3D') return;
          svg.selectAll('*').interrupt();
          lastDx = 0;
          lastDy = 0;
        })
        .on('drag', function(event) {
          if (!globeRef.current || mapProjection !== '3D') return;
          const r = projection.rotate();
          projection.rotate([
            r[0] + event.dx * dragSensitivity,
            Math.max(-90, Math.min(90, r[1] - event.dy * dragSensitivity)),
            r[2]
          ]);
          lastDx = event.dx;
          lastDy = event.dy;
          redrawGlobe();
        })
        .on('end', function() {
          if (!globeRef.current || mapProjection !== '3D') return;
          if (Math.abs(lastDx) < 1 && Math.abs(lastDy) < 1) return;
          const startR = projection.rotate();
          const target = [
            startR[0] + lastDx * dragSensitivity * 6,
            Math.max(-90, Math.min(90, startR[1] - lastDy * dragSensitivity * 6)),
            startR[2]
          ];
          d3.transition().duration(600).ease(d3.easeQuadOut)
            .tween('momentum', function() {
              return function(t) {
                projection.rotate([
                  startR[0] + (target[0] - startR[0]) * t,
                  startR[1] + (target[1] - startR[1]) * t,
                  startR[2]
                ]);
                redrawGlobe();
              };
            });
        });

      // Double-click to reset zoom and rotation
      svg.on('dblclick', function() {
        if (!globeRef.current || mapProjection !== '3D') return;
        svg.selectAll('*').interrupt();
        const startScale = projection.scale();
        const startR = projection.rotate();
        d3.transition().duration(800).ease(d3.easeQuadOut)
          .tween('reset', function() {
            return function(t) {
              projection
                .scale(startScale + (150 - startScale) * t)
                .rotate([
                  startR[0] * (1 - t),
                  startR[1] * (1 - t),
                  startR[2] * (1 - t)
                ]);
              redrawGlobe();
            };
          });
        d3.select(this).transition().duration(800).ease(d3.easeQuadOut)
          .call(d3.zoom().transform, d3.zoomIdentity);
      });

      // Apply both drag and zoom behaviors
      svg.call(drag).call(zoom);

      console.log('Globe rendered with', countries.size(), 'countries');

    } catch (error) {
      console.error('Error rendering globe:', error);
    }
  }, [mapData, selectedMetric, selectedMineral, mapProjection]);

  // Global Strategic Exposure Map Rendering
  const renderMap = useCallback(() => {
    console.log('renderMap called, mapRef:', mapRef.current, 'svgRef:', svgRef.current, 'mapData:', mapData);
    
    if (!mapRef.current || !svgRef.current) {
      console.log('Missing required refs:', { mapRef: !!mapRef.current, svgRef: !!svgRef.current });
      return;
    }

    try {
      const width = mapRef.current.clientWidth || 800;
      const height = mapRef.current.clientHeight || 600;
      
      console.log('Map dimensions:', { width, height });

      // Clear previous map and tooltips
      d3.select(svgRef.current).selectAll("*").remove();
      d3.selectAll('.tooltip').remove();
      
      // Remove any existing drag listeners
      d3.select(svgRef.current).on('.drag', null);

      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background-color', '#ffffff');

      // Create tooltip
      const tooltip = d3.select('body')
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '8px')
        .style('border-radius', '4px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('z-index', '1000');

      // Create projection with smooth transitions
      let projection = d3.geoNaturalEarth1()
        .scale(150)
        .translate([width / 2, height / 2]);

      let path = d3.geoPath().projection(projection);

      // Create zoom group for zoomable content (only for Import Ratio metric)
      const zoomGroup = svg.append('g').attr('class', 'zoom-group');
      
      // Setup zoom behavior (only for Import Ratio metric)
      let zoom = null;
      if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
        const initialScale = 150;
        const initialTranslate = [width / 2, height / 2];
        
        zoom = d3.zoom()
          .scaleExtent([1, 8])
          .wheelDelta((event) => -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.012))
          .on('zoom', (event) => {
            const { transform } = event;
            const newScale = initialScale * transform.k;
            const newTranslateX = initialTranslate[0] + transform.x / transform.k;
            const newTranslateY = initialTranslate[1] + transform.y / transform.k;

            projection
              .scale(newScale)
              .translate([newTranslateX, newTranslateY]);

            path = d3.geoPath().projection(projection);

            zoomGroup.selectAll('.country').attr('d', path);

            zoomGroup.selectAll('.reserve-circle, .trade-restriction-circle').each(function(d) {
              const countryFeature = worldMapData.features.find(f => {
                const countryName = f.properties.name || f.properties.NAME;
                const countryField = d.name;
                return countryName && countryField &&
                  countryName.toLowerCase() === countryField.toLowerCase();
              });
              if (countryFeature) {
                const centroid = d3.geoCentroid(countryFeature);
                const [cx, cy] = projection(centroid);
                d3.select(this).attr('cx', cx).attr('cy', cy);
              }
            });
          });
        
        // Apply zoom to SVG (only for Import Ratio)
        svg.call(zoom);
      }

      // Add smooth transition for initial render
      svg.style('opacity', 0)
        .transition()
        .duration(300)
        .style('opacity', 1);

      // No fallback data - only use real API data
      
      // Handle different possible data structures from API
      let countriesData = [];
      if (mapData) {
        if (Array.isArray(mapData.countries)) {
          countriesData = mapData.countries;
        } else if (Array.isArray(mapData.data)) {
          countriesData = mapData.data;
        } else if (Array.isArray(mapData)) {
          countriesData = mapData;
        }
      }
      
      // If no API data, don't use fallback - show empty map
      if (countriesData.length === 0) {
        console.log('No API countries data available - showing empty map');
        countriesData = [];
      }
      
      // Transform API data to match expected format
      if (mapData && mapData.countries && mapData.countries.length > 0) {
        console.log('=== TRANSFORMING API DATA ===');
        console.log('Original countries data:', mapData.countries);
        
        countriesData = mapData.countries.map(country => {
          const transformed = {
            country: country.name,
            dependency_ratio: country.dependencyRatio || 0,
            world_reserves: country.worldReserves || 0,
            domestic_reserves: country.domesticReserves || 0,
            trade_restrictions: country.tradeRestrictions || country.trade_restrictions || 0,
            restrictionTypes: country.restrictionTypes || [],
            primary_restriction: country.primary_restriction || 'No Restrictions',
            color: country.color || '#059669',
            partner_risk: country.partnerRisk || 0,
            iso3: country.iso3,
            tradeData: country.tradeData,
            // Add additional fields for better country matching
            name: country.name,
            altNames: getCountryAltNames(country.name),
            // Add World Reserves specific fields - check multiple possible field names
            reserveValue: country.reserveValue || country.worldReserves || country.world_reserves || country.reserves || country.reserve_2024 || country.reserve_2023 || 0,
            prod_est_2024: country.prod_est_2024 || country.production_2024 || country.prod_2024 || null,
            prod_2023: country.prod_2023 || country.production_2023 || country.prod_2023 || null,
            dataSource: country.dataSource || country.source || 'Unknown'
          };
          
          if (selectedMineral === 'Lithium' || selectedMineral === 'Graphite' || selectedMetric === 'World Reserves') {
            console.log(`Original country data for ${country.name}:`, country);
            console.log(`Transformed country for ${selectedMineral} - ${selectedMetric}:`, country.name, transformed);
            console.log(`Reserve fields for ${country.name}:`, {
              reserveValue: country.reserveValue,
              worldReserves: country.worldReserves,
              world_reserves: country.world_reserves,
              reserves: country.reserves,
              reserve_2024: country.reserve_2024,
              reserve_2023: country.reserve_2023
            });
          }
          
          return transformed;
        });
        
        console.log('Transformed countriesData:', countriesData);
        console.log('=== END TRANSFORMATION ===');
      }
      
      console.log('Countries data for map:', countriesData);
      console.log('Map data structure:', mapData);

      // Color scale based on selected metric - create after we have countries data
      const getColorScale = () => {
        console.log('Creating color scale with countries data:', countriesData);
        
        // Map metric names to data properties
        const metricMap = {
          [METRIC_IMPORT_DEPENDENCY]: 'dependency_ratio',
          'World Reserves': 'world_reserves', 
          'Domestic Reserves': 'domestic_reserves',
          'Trade Restrictions': 'trade_restrictions',
          'Partner Risk': 'partner_risk'
        };
        
        const metricKey = metricMap[selectedMetric] || 'dependency_ratio';
        
        // For Import Ratio, use all values including 0 for proper scale calculation
        let values;
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          values = countriesData
            .map(d => d[metricKey])
            .filter(v => v !== null && v !== undefined && v !== 'N/A');
        } else {
          values = countriesData.map(d => d[metricKey] || 0);
        }
        
        const maxValue = values.length > 0 ? Math.max(...values) : 1;
        
        console.log('Color scale values:', { values, maxValue, selectedMetric, metricKey });
        
        // Use custom color scheme for Import Ratio and Trade Restrictions, default scheme for other metrics
        let colorScale;
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          // Use fixed percentage domain to match the legend (0-100%)
          // Adjusted to make low values more visible with better contrast
          colorScale = d3.scaleLinear()
            .domain([0, 5, 15, 30, 50, 100])
            .range([
              '#F0F9E8',      // Very light green for 0% (NA/no data)
              '#CCEBC5',      // Light green for 5%
              '#A8DDB5',      // Medium light green for 15%
              '#7BCCC4',      // Light blue-green for 30%
              '#43A2CA',      // Medium blue for 50%
              '#0868AC'       // Dark blue for 100%
            ]);
        } else if (selectedMetric === 'Trade Restrictions') {
          colorScale = d3.scaleLinear()
            .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
            .range([
              '#e5e7eb',      // Light gray for no restrictions
              '#fef3c7',      // Light yellow for low restrictions
              '#fbbf24',      // Yellow for medium restrictions
              '#f59e0b',      // Orange for high restrictions
              '#dc2626'       // Red for very high restrictions
            ]);
        } else {
          // Default color scheme for other metrics
          colorScale = d3.scaleLinear()
            .domain([0, maxValue * 0.25, maxValue * 0.5, maxValue * 0.75, maxValue])
            .range([
              '#e5e7eb',      // Light gray for no data
              '#86efac',      // Light green
              '#fde047',      // Light yellow
              '#60a5fa',      // Light blue
              '#fbbf24'       // Amber/yellow
            ]);
        }
        
        return colorScale;
      };

      const colorScale = getColorScale();

      // Draw countries (use zoomGroup for Import Ratio, otherwise use svg directly)
      const countryContainer = selectedMetric === METRIC_IMPORT_DEPENDENCY ? zoomGroup : svg;
      const countries = countryContainer.selectAll('.country')
        .data(worldMapData.features)
        .enter()
        .append('path')
        .attr('class', 'country')
        .attr('d', path)
        .attr('fill', d => {
          // For World Reserves, always return white
          if (selectedMetric === 'World Reserves') {
            return '#ffffff'; // White for all countries in World Reserves
          }
          
          // For Trade Restrictions metrics, use white color
          if (selectedMetric === 'Trade Restrictions') {
            return '#ffffff'; // White for all countries in Trade Restrictions
          }
          
          const countryName = d.properties.name || d.properties.NAME;
          
          const countryData = countriesData.find(c => {
            if (!c || !countryName) return false;
            
            // Try different possible country name fields
            const countryField = c.country || c.name || c.country_name || c.countryName;
            if (!countryField) return false;
            
            // Check exact match
            if (countryField.toLowerCase() === countryName.toLowerCase()) {
              return true;
            }
            
            // Check alternative names
            if (c.altNames && Array.isArray(c.altNames)) {
              return c.altNames.some(altName => 
                altName.toLowerCase() === countryName.toLowerCase()
              );
            }
            
            return false;
          });
          
          if (countryData) {
            // Map metric names to data properties
            const metricMap = {
              [METRIC_IMPORT_DEPENDENCY]: 'dependency_ratio',
              'World Reserves': 'world_reserves', 
              'Domestic Reserves': 'domestic_reserves',
              'Trade Restrictions': 'trade_restrictions',
              'Partner Risk': 'partner_risk'
            };
            
            const metricKey = metricMap[selectedMetric] || 'dependency_ratio';
            const value = countryData[metricKey];
            
            // For Import Ratio, return white for NA/null/undefined or 0 values
            if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
              if (value === null || value === undefined || value === 'N/A' || value === 0) {
                console.log(`✅ Matched: ${countryName} -> ${countryData.country} (${metricKey}: ${value}) -> WHITE`);
                return '#ffffff';
              }
            }
            
            const actualValue = value || 0;
            const color = colorScale(actualValue);
            console.log(`✅ Matched: ${countryName} -> ${countryData.country} (${metricKey}: ${actualValue})`);
            return color;
          }
          
          // For countries without data, return white for Import Ratio, otherwise use color scale with 0 value
          if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
            return '#ffffff';
          }
          return colorScale(0);
        })
        .attr('stroke', '#4b5563')
        .attr('stroke-width', 0.5)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this)
            .attr('stroke', '#374151')
            .attr('stroke-width', 2);
        })
        .on('mouseout', function() {
          d3.select(this)
            .attr('stroke', '#4b5563')
            .attr('stroke-width', 0.5);
        })
        .on('click', function(event, d) {
          // Store click coordinates for positioning the detail panel
          d.clickX = event.clientX;
          d.clickY = event.clientY;
          
          // Also store the map container's bounding rect for better positioning
          if (mapRef.current) {
            const mapRect = mapRef.current.getBoundingClientRect();
            d.mapOffsetX = mapRect.left;
            d.mapOffsetY = mapRect.top;
            
            // Adjust click coordinates relative to the map container
            d.clickXRelative = event.clientX - mapRect.left;
            d.clickYRelative = event.clientY - mapRect.top;
          }
          
          handleCountryClick(d, countriesData);
        });

      console.log('Map rendered with', countries.size(), 'countries');
      
      // Add circular overlays for World Reserves metric
      if (selectedMetric === 'World Reserves') {
        // Create gradient definition for circles
        const circleGradient = svg.append('defs')
          .append('radialGradient')
          .attr('id', 'circleGradient');
        
        circleGradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', '#f0f9e8');
        
        circleGradient.append('stop')
          .attr('offset', '50%')
          .attr('stop-color', '#fde047');
        
        circleGradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', '#fbbf24');

        // Debug: Log the countries data
        console.log('Countries data for World Reserves:', countriesData);
        
        // No gradient needed - using solid color with transparency

        // Debug: Log the actual data structure
        console.log('Full countriesData for World Reserves:', countriesData);
        console.log('Sample country data:', countriesData[0]);
        
        // Check what fields are available for reserves
        const sampleCountry = countriesData[0];
        if (sampleCountry) {
          console.log('Available fields in country data:', Object.keys(sampleCountry));
          console.log('Reserve-related fields:', {
            reserveValue: sampleCountry.reserveValue,
            world_reserves: sampleCountry.world_reserves,
            reserve_2024: sampleCountry.reserve_2024,
            reserveValue: sampleCountry.reserveValue
          });
        }
        
        // Use only real database data - check multiple possible field names
        const reserveData = countriesData.filter(d => {
          const hasReserves = (d.reserveValue && d.reserveValue > 0) ||
                             (d.world_reserves && d.world_reserves > 0) ||
                             (d.reserve_2024 && d.reserve_2024 > 0) ||
                             (d.reserveValue && d.reserveValue > 0);
          
          if (hasReserves) {
            console.log('Found country with reserves:', d.name, {
              reserveValue: d.reserveValue,
              world_reserves: d.world_reserves,
              reserve_2024: d.reserve_2024
            });
          }
          
          return hasReserves;
        });
        
        console.log('Countries with reserves from database:', reserveData.length);
        console.log('Reserve data:', reserveData);
        
        // Only use real API data - no fallback data

        const circles = svg.selectAll('.reserve-circle')
          .data(reserveData)
          .enter()
          .append('circle')
          .attr('class', 'reserve-circle')
          .filter(d => {
            // Only show circles for countries that have valid coordinates
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              return countryName && d.name && 
                countryName.toLowerCase() === d.name.toLowerCase();
            });
            return countryFeature !== undefined;
          })
          .attr('cx', d => {
            // Get country center coordinates from the map data
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name; // Use 'name' field from database
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              console.log(`Country ${d.name} positioned at:`, { x, y });
              return x;
            }
            
            // If country not found in map data, skip it
            console.log(`Country ${d.name} not found in map data, skipping`);
            return null;
          })
          .attr('cy', d => {
            // Get country center coordinates from the map data
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name; // Use 'name' field from database
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return y;
            }
            
            // If country not found in map data, skip it
            return null;
          })
          .attr('r', d => {
            const value = d.reserveValue || 0; // Use reserveValue from database
            // Scale circle size based on reserves value (minimum 8px, maximum 60px)
            const minRadius = 8;
            const maxRadius = 60;
            const maxValue = Math.max(...reserveData.map(c => c.reserveValue || 0));
            if (maxValue === 0) return minRadius;
            return minRadius + (value / maxValue) * (maxRadius - minRadius);
          })
          .attr('fill', '#254b8c')
          .attr('fill-opacity', 0.7)
          .attr('stroke', '#1e40af')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.8)
          .style('cursor', 'pointer')
          .on('mouseover', function(event, d) {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3)
              .attr('stroke-opacity', 1);
              
            // Show tooltip with real data
            tooltip
              .style('opacity', 1)
              .html(`
                <div class="bg-gray-800 text-white p-2 rounded shadow-lg">
                  <div class="font-semibold">${d.name}</div>
                  <div class="text-sm">Reserves: ${d.reserveValue.toLocaleString()} tonnes</div>
                  <div class="text-sm">Production: ${(d.prod_est_2024 || d.prod_2023 || 'N/A')} tonnes</div>
                  <div class="text-xs text-gray-300">Source: ${d.dataSource || 'MCS2025'}</div>
                </div>
              `)
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 1.5)
              .attr('stroke-opacity', 0.8);
              
            // Hide tooltip
            tooltip.style('opacity', 0);
          })
          .on('click', function(event, d) {
            // Create a mock country object for the click handler
            const mockCountry = {
              properties: { name: d.name }, // Use 'name' field from database
              clickX: event.clientX,
              clickY: event.clientY,
              clickXRelative: event.clientX - (mapRef.current?.getBoundingClientRect().left || 0),
              clickYRelative: event.clientY - (mapRef.current?.getBoundingClientRect().top || 0)
            };
            handleCountryClick(mockCountry, reserveData);
          });
        
        console.log('Added', circles.size(), 'reserve circles with gradients');
      }
      
      // Add circular overlays for Trade Restrictions metric
      if (selectedMetric === 'Trade Restrictions') {
        // Use all countries data as they are already filtered for trade restrictions
        const tradeRestrictionData = countriesData;
        
        console.log('Rendering trade restriction circles for', tradeRestrictionData.length, 'countries');
        tradeRestrictionData.forEach(d => {
          console.log('Country with trade restrictions:', d.name, {
            trade_restrictions: d.trade_restrictions,
            tradeRestrictions: d.tradeRestrictions,
            restrictionTypes: d.restrictionTypes,
            primary_restriction: d.primary_restriction,
            color: d.color
          });
        });
        
        console.log('Countries with trade restrictions from database:', tradeRestrictionData.length);
        console.log('Trade restriction data:', tradeRestrictionData);
        
        // Only use real API data - no fallback data
        
        // Color mapping for different restriction types
        const restrictionTypeColors = {
          'Export Prohibition': '#dc2626',      // Red
          'Export Quota': '#f59e0b',            // Orange
          'Export Tax': '#fbbf24',              // Yellow
          'Export License Required': '#10b981', // Lime Green
          'Import Prohibition': '#dc2626',      // Red
          'Import Quota': '#f59e0b',            // Orange
          'Import Tax': '#fbbf24',              // Yellow
          'Import License Required': '#10b981', // Lime Green
          'Technical Barriers': '#ec4899',      // Pink
          'Sanitary Measures': '#ec4899',       // Pink
          'Other': '#ec4899',                   // Pink
          'No Restrictions': '#059669',         // Dark Green
          'Not Researched': '#6b7280'           // Gray
        };
        
        const circles = svg.selectAll('.trade-restriction-circle')
          .data(tradeRestrictionData)
          .enter()
          .append('circle')
          .attr('class', 'trade-restriction-circle')
          .filter(d => {
            // Only show circles for countries that have valid coordinates
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              return countryName && d.name && 
                countryName.toLowerCase() === d.name.toLowerCase();
            });
            return countryFeature !== undefined;
          })
          .attr('cx', d => {
            // Get country center coordinates from the map data
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              console.log(`Country ${d.name} positioned at:`, { x, y });
              return x;
            }
            
            return null;
          })
          .attr('cy', d => {
            const countryFeature = worldMapData.features.find(f => {
              const countryName = f.properties.name || f.properties.NAME;
              const countryField = d.name;
              return countryName && countryField && 
                countryName.toLowerCase() === countryField.toLowerCase();
            });
            
            if (countryFeature) {
              const centroid = d3.geoCentroid(countryFeature);
              const [x, y] = projection(centroid);
              return y;
            }
            
            return null;
          })
          .attr('r', 8) // Fixed radius for all circles
          .attr('fill', d => {
            // Use the color from API data if available
            if (d.color) {
              return d.color;
            }
            
            // Fallback to restriction type colors
            if (d.primary_restriction) {
              return restrictionTypeColors[d.primary_restriction] || restrictionTypeColors['Other'];
            }
            
            // Fallback to restriction types array
            if (d.restrictionTypes && d.restrictionTypes.length > 0) {
              const firstType = d.restrictionTypes[0].type || d.restrictionTypes[0];
              return restrictionTypeColors[firstType] || restrictionTypeColors['Other'];
            }
            
            // Default fallback
            return restrictionTypeColors['Not Researched'];
          })
          .attr('fill-opacity', 0.8)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('stroke-opacity', 0.9)
          .style('cursor', 'pointer')
          .on('mouseover', function(event, d) {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 3)
              .attr('stroke-opacity', 1);
              
            // Show tooltip with trade restriction data
            const restrictionTypes = d.restrictionTypes || [];
            const restrictionCount = d.trade_restrictions || d.tradeRestrictions || 0;
            const primaryRestriction = d.primary_restriction || 'No Restrictions';
            
            tooltip
              .style('opacity', 1)
              .html(`
                <div class="bg-gray-800 text-white p-2 rounded shadow-lg">
                  <div class="font-semibold">${d.name}</div>
                  <div class="text-sm">Restriction: ${primaryRestriction}</div>
                  ${restrictionTypes.length > 0 ? `
                    <div class="text-sm">Types: ${restrictionTypes.map(rt => rt.type || rt).join(', ')}</div>
                  ` : ''}
                </div>
              `)
              .style('left', (event.pageX + 10) + 'px')
              .style('top', (event.pageY - 10) + 'px');
          })
          .on('mouseout', function() {
            d3.select(this)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 1.5)
              .attr('stroke-opacity', 0.9);
              
            tooltip.style('opacity', 0);
          })
          .on('click', function(event, d) {
            // Create a mock country object for the click handler
            const mockCountry = {
              properties: { name: d.name },
              clickX: event.clientX,
              clickY: event.clientY,
              clickXRelative: event.clientX - (mapRef.current?.getBoundingClientRect().left || 0),
              clickYRelative: event.clientY - (mapRef.current?.getBoundingClientRect().top || 0)
            };
            handleCountryClick(mockCountry, tradeRestrictionData);
          });
        
        console.log('Added', circles.size(), 'trade restriction circles');
      }
      
      // Add zoom controls (only for Import Ratio metric)
      if (selectedMetric === METRIC_IMPORT_DEPENDENCY && zoom) {
        const zoomControls = svg.append('g')
          .attr('class', 'zoom-controls')
          .attr('transform', `translate(20, 20)`);
        
        // Zoom in button
        const zoomIn = zoomControls.append('g')
          .attr('class', 'zoom-control zoom-in')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 0)')
          .on('click', () => {
            svg.transition().call(
              zoom.scaleBy, 1.5
            );
          });
        
        zoomIn.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        zoomIn.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '18px')
          .attr('fill', '#4b5563')
          .text('+');
        
        // Zoom out button
        const zoomOut = zoomControls.append('g')
          .attr('class', 'zoom-control zoom-out')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 35)')
          .on('click', () => {
            svg.transition().call(
              zoom.scaleBy, 1 / 1.5
            );
          });
        
        zoomOut.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        zoomOut.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '18px')
          .attr('fill', '#4b5563')
          .text('−');
        
        // Reset zoom button
        const resetZoom = zoomControls.append('g')
          .attr('class', 'zoom-control reset-zoom')
          .style('cursor', 'pointer')
          .attr('transform', 'translate(0, 70)')
          .on('click', () => {
            svg.transition().duration(750).call(
              zoom.transform,
              d3.zoomIdentity
            );
          });
        
        resetZoom.append('rect')
          .attr('width', 30)
          .attr('height', 30)
          .attr('rx', 4)
          .attr('fill', 'white')
          .attr('stroke', '#4b5563')
          .attr('stroke-width', 1);
        
        resetZoom.append('text')
          .attr('x', 15)
          .attr('y', 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '14px')
          .attr('fill', '#4b5563')
          .text('⌂');
        
        // Hover effects
        zoomControls.selectAll('.zoom-control')
          .on('mouseover', function() {
            d3.select(this).select('rect')
              .attr('fill', '#f3f4f6')
              .attr('stroke', '#2563eb');
            d3.select(this).select('text')
              .attr('fill', '#2563eb');
          })
          .on('mouseout', function() {
            d3.select(this).select('rect')
              .attr('fill', 'white')
              .attr('stroke', '#4b5563');
            d3.select(this).select('text')
              .attr('fill', '#4b5563');
          });
      }

      // Add color legend (only for non-World Reserves, non-Trade Restrictions, and non-Domestic Reserves metrics)
      if (selectedMetric !== 'World Reserves' && selectedMetric !== 'Trade Restrictions' && selectedMetric !== 'Domestic Reserves') {
        const legendWidth = 200;
        const legendHeight = 20;
        const legendX = width - legendWidth - 20;
        const legendY = height - 40;

        const legend = svg.append('g')
          .attr('class', 'legend')
          .attr('transform', `translate(${legendX}, ${legendY})`);

        // Create gradient with colors based on selected metric
        const gradient = svg.append('defs')
          .append('linearGradient')
          .attr('id', 'mapGradient')
          .attr('x1', '0%')
          .attr('x2', '100%')
          .attr('y1', '0%')
          .attr('y2', '0%');

        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
          // Custom gradient for Import Ratio - matches the new color scale [0, 5, 15, 30, 50, 100]
          gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#F0F9E8')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '10%')
            .attr('stop-color', '#CCEBC5')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '30%')
            .attr('stop-color', '#A8DDB5')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '60%')
            .attr('stop-color', '#7BCCC4')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '80%')
            .attr('stop-color', '#43A2CA')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#0868AC')
            .attr('stop-opacity', 1);
        } else if (selectedMetric === 'Trade Restrictions') {
          // Custom gradient for Trade Restrictions
          gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#e5e7eb')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '25%')
            .attr('stop-color', '#fef3c7')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '50%')
            .attr('stop-color', '#fbbf24')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '75%')
            .attr('stop-color', '#f59e0b')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#dc2626')
            .attr('stop-opacity', 1);
        } else {
          // Default gradient for other metrics
          gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#e5e7eb')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '25%')
            .attr('stop-color', '#86efac')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '50%')
            .attr('stop-color', '#fde047')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '75%')
            .attr('stop-color', '#60a5fa')
            .attr('stop-opacity', 1);

          gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#fbbf24')
            .attr('stop-opacity', 1);
        }

        // Legend rectangle
        legend.append('rect')
          .attr('width', legendWidth)
          .attr('height', legendHeight)
          .attr('fill', 'url(#mapGradient)')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1);

        // Legend labels with percentage values
        if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
        // Specific labels for Import Ratio - adjusted for better low-value visibility
        legend.append('text')
          .attr('x', 0)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('font-weight', '500')
          .text('0');

          legend.append('text')
          .attr('x', legendWidth / 5)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('5');

          legend.append('text')
          .attr('x', 2 * legendWidth / 5)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('15');

          legend.append('text')
          .attr('x', 3 * legendWidth / 5)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('30');

          legend.append('text')
          .attr('x', 4 * legendWidth / 5)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('50');

          legend.append('text')
          .attr('x', legendWidth)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'end')
          .attr('font-weight', '500')
          .text('100');
        } else if (selectedMetric === 'Trade Restrictions') {
          // Specific labels for Trade Restrictions
          legend.append('text')
            .attr('x', 0)
            .attr('y', -5)
            .attr('font-size', '12px')
            .attr('fill', '#374151')
            .attr('font-weight', '500')
            .text('None');

          legend.append('text')
            .attr('x', legendWidth / 4)
            .attr('y', -5)
            .attr('font-size', '12px')
            .attr('fill', '#374151')
            .attr('text-anchor', 'middle')
            .attr('font-weight', '500')
            .text('Low');

          legend.append('text')
            .attr('x', legendWidth / 2)
            .attr('y', -5)
            .attr('font-size', '12px')
            .attr('fill', '#374151')
            .attr('text-anchor', 'middle')
            .attr('font-weight', '500')
            .text('Medium');

          legend.append('text')
            .attr('x', 3 * legendWidth / 4)
            .attr('y', -5)
            .attr('font-size', '12px')
            .attr('fill', '#374151')
            .attr('text-anchor', 'middle')
            .attr('font-weight', '500')
            .text('High');

          legend.append('text')
            .attr('x', legendWidth)
            .attr('y', -5)
            .attr('font-size', '12px')
            .attr('fill', '#374151')
            .attr('text-anchor', 'end')
            .attr('font-weight', '500')
            .text('Very High');
      } else if (selectedMetric === 'World Reserves') {
        // Create gradient legend for World Reserves
        const worldReservesGradient = svg.append('defs')
          .append('linearGradient')
          .attr('id', 'worldReservesGradient')
          .attr('x1', '0%')
          .attr('x2', '100%')
          .attr('y1', '0%')
          .attr('y2', '0%');

        worldReservesGradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', '#f0f9e8');

        worldReservesGradient.append('stop')
          .attr('offset', '25%')
          .attr('stop-color', '#86efac');

        worldReservesGradient.append('stop')
          .attr('offset', '50%')
          .attr('stop-color', '#fde047');

        worldReservesGradient.append('stop')
          .attr('offset', '75%')
          .attr('stop-color', '#60a5fa');

        worldReservesGradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', '#fbbf24');

        // Legend rectangle with gradient
        legend.append('rect')
          .attr('width', legendWidth)
          .attr('height', legendHeight)
          .attr('fill', 'url(#worldReservesGradient)')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1);

        // Legend labels for World Reserves
        legend.append('text')
          .attr('x', 0)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('font-weight', '500')
          .text('0%');

        legend.append('text')
          .attr('x', legendWidth / 4)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('25%');

        legend.append('text')
          .attr('x', legendWidth / 2)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('50%');

        legend.append('text')
          .attr('x', 3 * legendWidth / 4)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '500')
          .text('75%');

        legend.append('text')
          .attr('x', legendWidth)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'end')
          .attr('font-weight', '500')
          .text('100%');
      } else {
        // Default labels for other metrics
        legend.append('text')
          .attr('x', 0)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('font-weight', '500')
          .text('Low');

        legend.append('text')
          .attr('x', legendWidth)
          .attr('y', -5)
          .attr('font-size', '12px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'end')
          .attr('font-weight', '500')
          .text('High');
      }

        legend.append('text')
          .attr('x', legendWidth / 2)
          .attr('y', legendHeight + 15)
          .attr('font-size', '10px')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .attr('font-weight', '600')
          .text(selectedMetric);
      }
      
      // Add custom legend for Trade Restrictions
      if (selectedMetric === 'Trade Restrictions') {
        const legendWidth = 200;
        const legendHeight = 20;
        const legendX = 20; // Position at the left edge
        const legendY = height - 200; // Position at the bottom to utilize available space
        
        const legend = svg.append('g')
          .attr('class', 'trade-restrictions-legend')
          .attr('transform', `translate(${legendX}, ${legendY})`);
        
        // Add background rectangle for better readability
        legend.append('rect')
          .attr('x', -10)
          .attr('y', -25)
          .attr('width', legendWidth + 20)
          .attr('height', 200)
          .attr('fill', isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)')
          .attr('stroke', isDarkMode ? '#4b5563' : '#d1d5db')
          .attr('stroke-width', 1)
          .attr('rx', 8);
        
        // Legend title
        legend.append('text')
          .attr('x', 0)
          .attr('y', -10)
          .attr('font-size', '12px')
          .attr('fill', isDarkMode ? '#e5e7eb' : '#374151')
          .attr('font-weight', '600')
          .text('Trade Restrictions');
        
        // Restriction type colors
        const restrictionTypes = [
          { name: 'Export Prohibition', color: '#dc2626' },
          { name: 'Export Quota', color: '#f59e0b' },
          { name: 'Export Tax', color: '#fbbf24' },
          { name: 'Licensing Requirement', color: '#10b981' },
          { name: 'Other Measures', color: '#ec4899' },
          { name: 'No Restrictions', color: '#059669' },
          { name: 'Not Researched', color: '#6b7280' }
        ];
        
        restrictionTypes.forEach((type, index) => {
          const itemY = index * 25;
          
          // Color circle
          legend.append('circle')
            .attr('cx', 8)
            .attr('cy', itemY + 8)
            .attr('r', 6)
            .attr('fill', type.color)
            .attr('stroke', isDarkMode ? '#374151' : '#ffffff')
            .attr('stroke-width', 1);
          
          // Label
          legend.append('text')
            .attr('x', 20)
            .attr('y', itemY + 12)
            .attr('font-size', '11px')
            .attr('fill', isDarkMode ? '#e5e7eb' : '#374151')
            .text(type.name);
        });
      }

    } catch (error) {
      console.error('Error rendering map:', error);
    }
  }, [mapData, selectedMetric, selectedMineral]);

  useEffect(() => {
    if (mapRef.current && svgRef.current) {
      // Render map immediately, then re-render when data changes
      renderMap();
    }
  }, [renderMap]);

  // Also render map when data loads
  useEffect(() => {
    if (mapRef.current && svgRef.current && mapData) {
      renderMap();
    }
  }, [mapData, renderMap]);

  // Re-render map when switching to Map view
  useEffect(() => {
    if (selectedView === 'Map' && mapProjection === '2D' && mapRef.current && svgRef.current && mapData) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        renderMap();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedView, mapProjection, mapData, renderMap]);

  // Globe rendering effects
  useEffect(() => {
    if (mapProjection === '3D' && selectedView === 'Map') {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (globeRef.current && globeSvgRef.current) {
          renderGlobe();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [renderGlobe, mapProjection, selectedView]);

  useEffect(() => {
    if (mapProjection === '3D' && selectedView === 'Map' && mapData) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (globeRef.current && globeSvgRef.current) {
          renderGlobe();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [mapData, renderGlobe, mapProjection, selectedView]);

  // Function to fetch domestic reserves data
  const loadDomesticReservesData = useCallback(async () => {
    try {
      console.log('=== LOADING DOMESTIC RESERVES DATA ===');
      console.log('Selected mineral:', selectedMineral);
      console.log('API endpoint: /api/domestic-reserves');
      
      const response = await fetch('/api/domestic-reserves');
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('=== DOMESTIC RESERVES API RESPONSE ===');
      console.log('Full API response:', data);
      console.log('Response success:', data.success);
      console.log('Response data exists:', !!data.data);
      console.log('Response data type:', typeof data.data);
      console.log('Response data keys:', Object.keys(data.data || {}));
      
      if (data.success && data.data) {
        let allReserves = [];
        
        // Handle the expected API format: data.states array
        if (data.data.states && Array.isArray(data.data.states)) {
          console.log('Processing data.states format');
          console.log('Number of states:', data.data.states.length);
          console.log('Sample state:', data.data.states[0]);
          
          // Extract reserves from each state
          data.data.states.forEach((state, stateIndex) => {
            console.log(`Processing state ${stateIndex}:`, state.state);
            console.log('State reserves array:', state.reserves);
            
            if (state.reserves && Array.isArray(state.reserves)) {
              state.reserves.forEach((reserve, reserveIndex) => {
                console.log(`Processing reserve ${reserveIndex} for ${state.state}:`, reserve);
                console.log('Reserve mineral:', reserve.mineral);
                console.log('Selected mineral:', selectedMineral);
                console.log('Mineral match:', selectedMineral === 'All' || reserve.mineral === selectedMineral);
                
                // Filter by selected mineral
                const mineralMatch = selectedMineral === 'All' || reserve.mineral === selectedMineral;
                console.log(`Mineral filtering: ${reserve.mineral} === ${selectedMineral} = ${mineralMatch}`);
                
                if (mineralMatch) {
                  const reserveRecord = {
                    ...reserve,
                    state: state.state // Ensure state name is set
                  };
                  console.log('Adding reserve record:', reserveRecord);
                  allReserves.push(reserveRecord);
                } else {
                  console.log(`Skipping reserve for ${reserve.mineral} (not matching ${selectedMineral})`);
                }
              });
            } else {
              console.log(`No reserves array found for state: ${state.state}`);
            }
          });
        }
        // Fallback: if data is directly an array
        else if (Array.isArray(data.data)) {
          allReserves = data.data.filter(reserve => 
            selectedMineral === 'All' || reserve.mineral === selectedMineral
          );
        }
        // Fallback: if data has a 'reserves' property
        else if (data.data.reserves && Array.isArray(data.data.reserves)) {
          allReserves = data.data.reserves.filter(reserve => 
            selectedMineral === 'All' || reserve.mineral === selectedMineral
          );
        }
        
        console.log('=== PROCESSED DOMESTIC RESERVES DATA ===');
        console.log('Total reserves found:', allReserves.length);
        console.log('All reserves data:', allReserves);
        
        if (allReserves.length > 0) {
          console.log('=== SAMPLE DOMESTIC RESERVE RECORD ===');
          console.log('Sample record:', allReserves[0]);
          console.log('Available fields in sample record:', Object.keys(allReserves[0] || {}));
          console.log('Sample record values:', Object.entries(allReserves[0] || {}));
        } else {
          console.log('⚠️ NO RESERVES DATA FOUND - This might be why table shows N/A');
        }
        
        setDomesticReservesData(allReserves);
      } else {
        console.error('No domestic reserves data available');
        setDomesticReservesData([]);
      }
    } catch (error) {
      console.error('=== ERROR LOADING DOMESTIC RESERVES DATA ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setDomesticReservesData([]);
    }
  }, [selectedMineral]);

  // Load domestic reserves or production data based on domesticReservesMineral and domesticReservesType
  useEffect(() => {
    // Always load domestic reserves/production data for the dedicated section
    const loadData = async () => {
      const dataType = domesticReservesType === 'Production' ? 'PRODUCTION' : 'RESERVES';
      try {
        console.log(`=== LOADING DOMESTIC ${dataType} DATA ===`);
        console.log('Selected mineral:', domesticReservesMineral);
        console.log('Selected type:', domesticReservesType);
        
        const endpoint = domesticReservesType === 'Production' ? '/api/domestic-production' : '/api/domestic-reserves';
        console.log('API endpoint:', endpoint);
        
        const response = await fetch(endpoint);
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`=== DOMESTIC ${dataType} API RESPONSE ===`);
        console.log('Full API response:', data);
        console.log('Response success:', data.success);
        console.log('Response data exists:', !!data.data);
        console.log('Response data type:', typeof data.data);
        console.log('Response data keys:', Object.keys(data.data || {}));
        
        if (data.success && data.data) {
          let allData = [];
          
          if (domesticReservesType === 'Production') {
            // Handle production data structure
            // Production data is country-level, so we need to handle it differently
            if (data.data.production && Array.isArray(data.data.production)) {
              console.log('Processing production data:', data.data.production);
              // Filter by selected mineral
              const filteredProduction = data.data.production.filter(prod => 
                domesticReservesMineral === 'All' || prod.mineral === domesticReservesMineral
              );
              
              // Convert production data to a format similar to reserves
              // Note: Production data doesn't have state information, so we'll create a placeholder
              // TODO: If production data has state information in the future, update this logic
              filteredProduction.forEach(prod => {
                allData.push({
                  mineral: prod.mineral,
                  quantity: prod.quantity,
                  reserve_quantity: prod.quantity, // Use quantity as reserve_quantity for compatibility
                  year: prod.year,
                  unit: prod.unit,
                  country: prod.country || 'India',
                  state: 'India', // Placeholder since production is country-level
                  data_source: prod.data_source || 'domestic_production'
                });
              });
              
              console.log('Processed production data:', allData);
              console.warn('⚠️ Production data is country-level, not state-level. Map visualization may not work as expected.');
            } else {
              console.warn('Production data format not recognized');
              allData = [];
            }
          } else {
            // Handle reserves data structure (existing logic)
            // Handle the expected API format: data.states array
            if (data.data.states && Array.isArray(data.data.states)) {
              console.log('Processing data.states format');
              console.log('Number of states:', data.data.states.length);
              console.log('Sample state:', data.data.states[0]);
              
              // Extract reserves from each state
              data.data.states.forEach((state, stateIndex) => {
                console.log(`Processing state ${stateIndex}:`, state.state);
                console.log('State reserves array:', state.reserves);
                
                if (state.reserves && Array.isArray(state.reserves)) {
                  state.reserves.forEach((reserve, reserveIndex) => {
                    console.log(`Processing reserve ${reserveIndex} for ${state.state}:`, reserve);
                    console.log('Reserve mineral:', reserve.mineral);
                    console.log('Selected mineral:', domesticReservesMineral);
                    console.log('Mineral match:', domesticReservesMineral === 'All' || reserve.mineral === domesticReservesMineral);
                    
                    // Filter by selected mineral
                    const mineralMatch = domesticReservesMineral === 'All' || reserve.mineral === domesticReservesMineral;
                    console.log(`Mineral filtering: ${reserve.mineral} === ${domesticReservesMineral} = ${mineralMatch}`);
                    
                    if (mineralMatch) {
                      const reserveRecord = {
                        ...reserve,
                        state: state.state // Ensure state name is set
                      };
                      console.log('Adding reserve record:', reserveRecord);
                      allData.push(reserveRecord);
                    } else {
                      console.log(`Skipping reserve for ${reserve.mineral} (not matching ${domesticReservesMineral})`);
                    }
                  });
                } else {
                  console.log(`No reserves array found for state: ${state.state}`);
                }
              });
            }
            // Fallback: if data is directly an array
            else if (Array.isArray(data.data)) {
              allData = data.data.filter(reserve => 
                domesticReservesMineral === 'All' || reserve.mineral === domesticReservesMineral
              );
            }
            // Fallback: if data has a 'reserves' property
            else if (data.data.reserves && Array.isArray(data.data.reserves)) {
              allData = data.data.reserves.filter(reserve => 
                domesticReservesMineral === 'All' || reserve.mineral === domesticReservesMineral
              );
            }
          }
          
          console.log(`=== PROCESSED DOMESTIC ${dataType} DATA ===`);
          console.log(`Total ${domesticReservesType.toLowerCase()} found:`, allData.length);
          console.log(`All ${domesticReservesType.toLowerCase()} data:`, allData);
          
          if (allData.length > 0) {
            console.log(`=== SAMPLE DOMESTIC ${dataType} RECORD ===`);
            console.log('Sample record:', allData[0]);
            console.log('Available fields in sample record:', Object.keys(allData[0] || {}));
            console.log('Sample record values:', Object.entries(allData[0] || {}));
          } else {
            console.log(`⚠️ NO ${dataType} DATA FOUND - This might be why table shows N/A`);
          }
          
          setDomesticReservesData(allData);
        } else {
          console.error(`No domestic ${domesticReservesType.toLowerCase()} data available`);
          setDomesticReservesData([]);
        }
      } catch (error) {
        console.error(`=== ERROR LOADING DOMESTIC ${dataType} DATA ===`);
        console.error('Error details:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        setDomesticReservesData([]);
      }
    };
    
    loadData();
  }, [domesticReservesMineral, domesticReservesType]);

  // Load ALL domestic reserves data for KPI calculations (not filtered by mineral)
  useEffect(() => {
    const loadAllReservesData = async () => {
      try {
        console.log('=== LOADING ALL DOMESTIC RESERVES DATA FOR KPIs ===');
        
        const response = await fetch('/api/domestic-reserves');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.data) {
          let allReserves = [];
          
          // Handle the expected API format: data.states array
          if (data.data.states && Array.isArray(data.data.states)) {
            // Extract ALL reserves from each state (no filtering)
            data.data.states.forEach((state) => {
              if (state.reserves && Array.isArray(state.reserves)) {
                state.reserves.forEach((reserve) => {
                  allReserves.push({
                    ...reserve,
                    state: state.state
                  });
                });
              }
            });
          }
          // Fallback: if data is directly an array
          else if (Array.isArray(data.data)) {
            allReserves = data.data;
          }
          // Fallback: if data has a 'reserves' property
          else if (data.data.reserves && Array.isArray(data.data.reserves)) {
            allReserves = data.data.reserves;
          }
          
          console.log('=== ALL RESERVES DATA FOR KPIs LOADED ===');
          console.log('Total reserves (all minerals):', allReserves.length);
          
          setDomesticReservesAllData(allReserves);
        } else {
          console.error('No domestic reserves data available for KPIs');
          setDomesticReservesAllData([]);
        }
      } catch (error) {
        console.error('=== ERROR LOADING ALL RESERVES DATA FOR KPIs ===');
        console.error('Error:', error);
        setDomesticReservesAllData([]);
      }
    };
    
    loadAllReservesData();
  }, []); // Load once when component mounts, no dependencies

  // Calculate Domestic Reserves KPIs (tonnage-based) - Uses ALL minerals data
  const calculateDomesticReservesKPIs = useMemo(() => {
    if (!domesticReservesAllData || domesticReservesAllData.length === 0) return null;
    
    // ============================================
    // KPI 1: Top Reserve State (by tonnage) - ALL minerals
    // ============================================
    const stateReserves = {};
    domesticReservesAllData.forEach(item => {
      // Parse reserve_quantity properly, handling comma-separated strings
      let quantity = 0;
      if (item.reserve_quantity) {
        if (typeof item.reserve_quantity === 'string') {
          quantity = parseFloat(item.reserve_quantity.replace(/,/g, '')) || 0;
        } else {
          quantity = parseFloat(item.reserve_quantity) || 0;
        }
      }
      stateReserves[item.state] = (stateReserves[item.state] || 0) + quantity;
    });
    
    const sortedStates = Object.entries(stateReserves)
      .sort(([,a], [,b]) => b - a);
    const topState = sortedStates[0];
    const totalReserves = Object.values(stateReserves).reduce((a,b) => a+b, 0);
    
    // ============================================
    // KPI 2: Most Concentrated Mineral - ALL minerals
    // ============================================
    const mineralByState = {};
    domesticReservesAllData.forEach(item => {
      if (!mineralByState[item.mineral]) {
        mineralByState[item.mineral] = {};
      }
      // Parse reserve_quantity properly, handling comma-separated strings
      let quantity = 0;
      if (item.reserve_quantity) {
        if (typeof item.reserve_quantity === 'string') {
          quantity = parseFloat(item.reserve_quantity.replace(/,/g, '')) || 0;
        } else {
          quantity = parseFloat(item.reserve_quantity) || 0;
        }
      }
      mineralByState[item.mineral][item.state] = 
        (mineralByState[item.mineral][item.state] || 0) + quantity;
    });
    
    const concentrations = Object.entries(mineralByState).map(([mineral, states]) => {
      const total = Object.values(states).reduce((a,b) => a+b, 0);
      const topTwoStates = Object.entries(states)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 2);
      const topTwoShare = topTwoStates.reduce((sum, [,qty]) => sum + qty, 0) / total * 100;
      
      return {
        mineral,
        concentration: topTwoShare,
        topStates: topTwoStates.map(([state]) => state),
        breakdown: topTwoStates.map(([state, qty]) => ({
          state,
          tonnage: qty,
          percentage: ((qty / total) * 100).toFixed(1)
        }))
      };
    });
    
    const mostConcentrated = concentrations.sort((a,b) => b.concentration - a.concentration)[0];
    
    // ============================================
    // KPI 3: Total Identified Reserves - ALL minerals
    // ============================================
    const uniqueMinerals = new Set(domesticReservesAllData.map(i => i.mineral));
    
    // Breakdown by mineral for additional context
    const byMineral = {};
    domesticReservesAllData.forEach(item => {
      // Parse reserve_quantity properly, handling comma-separated strings
      let quantity = 0;
      if (item.reserve_quantity) {
        if (typeof item.reserve_quantity === 'string') {
          quantity = parseFloat(item.reserve_quantity.replace(/,/g, '')) || 0;
        } else {
          quantity = parseFloat(item.reserve_quantity) || 0;
        }
      }
      byMineral[item.mineral] = (byMineral[item.mineral] || 0) + quantity;
    });
    
    // Sort minerals by tonnage for all minerals
    const topMinerals = Object.entries(byMineral)
      .sort(([, a], [, b]) => b - a)
      .map(([mineral, tonnage]) => ({
        mineral,
        tonnage,
        percentage: ((tonnage / totalReserves) * 100).toFixed(1)
      }));
    
    return {
      topState: {
        name: topState[0],
        tonnage: topState[1],
        percentage: ((topState[1] / totalReserves) * 100).toFixed(1)
      },
      mostConcentrated: {
        mineral: mostConcentrated.mineral,
        concentration: mostConcentrated.concentration.toFixed(1),
        states: mostConcentrated.topStates.join(' & '),
        breakdown: mostConcentrated.breakdown
      },
      totalReserves: {
        tonnage: totalReserves,
        mineralsCount: uniqueMinerals.size,
        topMinerals
      }
    };
  }, [domesticReservesAllData]);

  // Trade Policy Tracker API functions
  const fetchTradePolicyMinerals = async () => {
    try {
      const response = await axios.get('/api/trade-policy-tracker/minerals');
      if (response.data.success) {
        setAvailableTradePolicyMinerals(response.data.minerals);
      }
    } catch (err) {
      console.error('Error fetching trade policy minerals:', err);
    }
  };

  const fetchTradePolicyData = useCallback(async () => {
    if (selectedMetric !== 'Trade Restrictions') return;
    
    try {
      setTradePolicyLoading(true);
      const params = {};
      if (selectedMineral) params.mineral = selectedMineral;
      // Removed year filter to show all policies for the selected mineral regardless of year
      const response = await axios.get('/api/trade-policy-tracker/kpis', { params });
      if (response.data.success) {
        setTradePolicyData(response.data.data);
        setTradePolicyError(null);
      } else {
        setTradePolicyError('Failed to fetch trade policy data');
      }
    } catch (err) {
      console.error('Error fetching trade policy data:', err);
      setTradePolicyError(err.message);
    } finally {
      setTradePolicyLoading(false);
    }
  }, [selectedMineral, selectedMetric]);

  // Load Trade Policy Tracker data
  useEffect(() => {
    fetchTradePolicyMinerals();
  }, []);

  useEffect(() => {
    if (selectedMetric === 'Trade Restrictions') {
      fetchTradePolicyData();
    }
  }, [selectedMineral, selectedMetric, fetchTradePolicyData]);

  // Function to get transformed data (same as map/globe)
  const getTransformedCountriesData = useCallback(() => {
    if (!mapData || !mapData.countries || mapData.countries.length === 0) {
      return [];
    }

    // Use the exact same transformation logic as the map/globe
    const countriesData = mapData.countries.map(country => {
      const transformed = {
        country: country.name,
        dependency_ratio: country.dependencyRatio || 0,
        world_reserves: country.worldReserves || 0,
        domestic_reserves: country.domesticReserves || 0,
        trade_restrictions: country.tradeRestrictions || country.trade_restrictions || 0,
        tradeRestrictions: country.tradeRestrictions || country.trade_restrictions || 0,
        restrictionTypes: country.restrictionTypes || [],
        primary_restriction: country.primary_restriction || 'No Restrictions',
        color: country.color || '#059669',
        partner_risk: country.partnerRisk || 0,
        iso3: country.iso3,
        name: country.name,
        // Add World Reserves specific fields - same as map
        reserveValue: country.reserveValue || country.worldReserves || country.world_reserves || country.reserves || country.reserve_2024 || country.reserve_2023 || 0,
        prod_est_2024: country.prod_est_2024 || country.production_2024 || country.prod_2024 || null,
        prod_2023: country.prod_2023 || country.production_2023 || country.prod_2023 || null,
        dataSource: country.dataSource || country.source || 'Unknown'
      };
      
      // Debug Lithium data specifically
      if (selectedMineral === 'Lithium' && country.name && country.name.toLowerCase().includes('jammu')) {
        console.log(`=== LITHIUM DEBUG for ${country.name} ===`);
        console.log('Original country data:', country);
        console.log('Transformed data:', transformed);
        console.log('Reserve fields check:', {
          reserveValue: country.reserveValue,
          worldReserves: country.worldReserves,
          world_reserves: country.world_reserves,
          reserves: country.reserves,
          reserve_2024: country.reserve_2024,
          reserve_2023: country.reserve_2023
        });
      }
      
      return transformed;
    });

    return countriesData;
  }, [mapData]);

  // Debug effect to log data changes
  useEffect(() => {
    console.log('=== Data Debug Info ===');
    console.log('mapData:', mapData);
    console.log('mapData.countries:', mapData?.countries);
    console.log('mapData.countries length:', mapData?.countries ? mapData.countries.length : 0);
    console.log('loading:', loading);
    console.log('selectedMetric:', selectedMetric);
    console.log('selectedMineral:', selectedMineral);
    console.log('selectedYear:', selectedYear);
    
    const transformedData = getTransformedCountriesData();
    console.log('Transformed data for table:', transformedData.slice(0, 3));
    
    if (transformedData.length > 0) {
      const withDependencyRatio = transformedData.filter(item => 
        item.dependency_ratio !== null && 
        item.dependency_ratio !== undefined && 
        item.dependency_ratio > 0
      );
      console.log('Countries with dependency_ratio:', withDependencyRatio.length);
      console.log('Sample dependency ratios:', withDependencyRatio.slice(0, 3).map(item => ({
        country: item.name,
        ratio: item.dependency_ratio
      })));
    }
    console.log('========================');
  }, [mapData, loading, selectedMetric, selectedMineral, selectedYear, getTransformedCountriesData]);

  // Cleanup effect to remove event listeners
  useEffect(() => {
    return () => {
      // Clean up D3 event listeners when component unmounts or view changes
      if (globeSvgRef.current) {
        d3.select(globeSvgRef.current).on('.drag', null).on('.zoom', null).on('dblclick', null);
      }
      if (svgRef.current) {
        d3.select(svgRef.current).on('.drag', null).on('.zoom', null).on('dblclick', null);
      }
      
      // Cancel any ongoing D3 transitions
      if (globeSvgRef.current) {
        d3.select(globeSvgRef.current).selectAll('*').interrupt();
      }
      if (svgRef.current) {
        d3.select(svgRef.current).selectAll('*').interrupt();
      }
    };
  }, [mapProjection, selectedView]);

  // Helper function to calculate KPI values for Import Ratio
  const calculateImportRatioKPIs = useCallback(() => {
    if (!mapData || !mapData.countries || selectedMetric !== METRIC_IMPORT_DEPENDENCY) {
      return null;
    }

    console.log('=== CALCULATING IMPORT RATIO KPIs ===');
    console.log('Map data countries:', mapData.countries);
    console.log('Sample country data:', mapData.countries[0]);

    const countriesData = mapData.countries.map(country => {
      console.log('Processing country:', country.name, country);
      console.log('All country fields:', Object.keys(country));
      console.log('Looking for mineral_world_import_value:', country.mineral_world_import_value);
      return {
        country: country.name,
        dependency_ratio: country.dependencyRatio || 0,
        mineral_world_import_value: country.mineral_world_import_value || 0
      };
    });

    console.log('Transformed countries data for KPI:', countriesData);

    // Calculate World Value using mineral_world_import_value from API
    let worldValue = 0;
    let worldValueLabel = 'World Value';
    
    // Get the mineral_world_import_value from the first country (should be the same for all countries for a given mineral/year)
    const worldImportValue = countriesData.find(country => country.mineral_world_import_value > 0)?.mineral_world_import_value || 0;
    
    if (worldImportValue > 0) {
      worldValue = worldImportValue;
      console.log('Using mineral_world_import_value for world value:', worldValue);
    } else {
      console.log('No mineral_world_import_value found in country data');
      console.log('Available fields in first country:', countriesData[0] ? Object.keys(countriesData[0]) : 'No data');
    }

    // Find highest importing country (highest dependency ratio)
    const highestImportingCountry = countriesData.reduce((max, country) => {
      if (country.dependency_ratio > max.dependency_ratio) {
        return country;
      }
      return max;
    }, { country: 'N/A', dependency_ratio: 0 });

    console.log('KPI Results:', {
      worldValue,
      worldValueLabel,
      highestImportingCountry: highestImportingCountry.country,
      highestImportRatio: highestImportingCountry.dependency_ratio
    });

    return {
      worldValue: worldValue > 0 ? worldValue : 'N/A',
      worldValueLabel: worldValueLabel,
      highestImportingCountry: highestImportingCountry.country,
      highestImportRatio: highestImportingCountry.dependency_ratio
    };
  }, [mapData, selectedMetric]);

  // Helper function to calculate KPI values for World Reserves
  const calculateWorldReservesKPIs = useCallback(() => {
    if (!mapData || !mapData.countries || selectedMetric !== 'World Reserves') {
      return null;
    }

    console.log('=== CALCULATING WORLD RESERVES KPIs ===');
    console.log('Map data countries:', mapData.countries);
    console.log('Sample country data:', mapData.countries[0]);

    const countriesData = mapData.countries
      .filter(country => 
        country.name !== 'World' && 
        country.country !== 'World' &&
        country.name !== 'World total (rounded)'
      )
      .map(country => {
        console.log('Processing country:', country.name, country);
        console.log('All country fields:', Object.keys(country));
        console.log('reserveValue field:', country.reserveValue);
        
        return {
          country: country.name,
          world_reserves: country.reserveValue || 0
        };
      });

    console.log('Transformed countries data for World Reserves KPI:', countriesData);

    // Calculate World Value using "World" country record from production_reserves table
    let worldValue = 0;
    let worldValueLabel = 'Total Reserves';
    
    // Look for the "World" country record in the original mapData.countries
    const worldCountryRecord = mapData.countries.find(country => 
      country.name === 'World' || 
      country.country === 'World' ||
      country.name === 'World total (rounded)' ||
      country.name === 'World total'
    );
    
    console.log('Looking for World country record...');
    console.log('All countries in mapData:', mapData.countries.map(c => ({ name: c.name, reserveValue: c.reserveValue })));
    console.log('Found World record:', worldCountryRecord);
    
    // Check if any country names contain "World" or "total"
    const worldLikeCountries = mapData.countries.filter(c => 
      c.name.toLowerCase().includes('world') || 
      c.name.toLowerCase().includes('total')
    );
    console.log('Countries containing "world" or "total":', worldLikeCountries);
    
    if (worldCountryRecord && worldCountryRecord.reserveValue) {
      worldValue = worldCountryRecord.reserveValue;
      console.log('Using World country record for world value:', worldValue);
      console.log('World country record details:', worldCountryRecord);
    } else {
      // Fallback: use totalReserves from mapData
      if (mapData.totalReserves && mapData.totalReserves > 0) {
        worldValue = mapData.totalReserves;
        console.log('Using totalReserves for world value:', worldValue);
      } else {
        // Second fallback: sum up all individual country reserves
        const totalFromCountries = mapData.countries.reduce((sum, country) => {
          return sum + (country.reserveValue || 0);
        }, 0);
        
        if (totalFromCountries > 0) {
          worldValue = totalFromCountries;
          console.log('Using sum of individual country reserves for world value:', worldValue);
          console.log('Individual country reserves:', mapData.countries.map(c => ({ name: c.name, reserveValue: c.reserveValue })));
        } else {
          console.log('No World country record, totalReserves, or individual country reserves found');
          console.log('Available countries:', mapData.countries.map(c => c.name));
          console.log('Available fields in mapData:', Object.keys(mapData));
        }
      }
    }

    // Find highest reserves country (highest world_reserves)
    console.log('Countries data for top reserves calculation:', countriesData);
    const highestReservesCountry = countriesData.reduce((max, country) => {
      console.log(`Comparing ${country.country}: ${country.world_reserves} vs current max: ${max.world_reserves}`);
      if (country.world_reserves > max.world_reserves) {
        console.log(`New max found: ${country.country} with ${country.world_reserves}`);
        return country;
      }
      return max;
    }, { country: 'N/A', world_reserves: 0 });
    
    console.log('Final highest reserves country:', highestReservesCountry);

    console.log('World Reserves KPI Results:', {
      worldValue,
      worldValueLabel,
      highestReservesCountry: highestReservesCountry.country,
      highestReserves: highestReservesCountry.world_reserves
    });

    return {
      worldValue: worldValue > 0 ? worldValue : 'N/A',
      worldValueLabel: worldValueLabel,
      highestReservesCountry: highestReservesCountry.country,
      highestReserves: highestReservesCountry.world_reserves
    };
  }, [mapData, selectedMetric]);

  // Helper function to get metric-specific content
  const getMetricContent = (metric) => {
    const metricContent = {
      [METRIC_IMPORT_DEPENDENCY]: {
        title: 'Import Dependency',
        description: 'This visualization shows import dependency of countries on critical minerals. It helps identify economies that rely heavily on imports, informing global assessments of supply-chain resilience and exposure. '
      },
      'World Reserves': {
        title: 'World Reserves',
        description: 'This map illustrates the global distribution of critical mineral reserves. It highlights how resource availability is unevenly spread across regions, emphasizing the importance of strategic partnerships and long-term resource planning.'
      },
      'Trade Restrictions': {
        title: 'Trade Restrictions',
        description: 'This map illustrates the global trade restrictions of critical minerals.'
      }
    };
    return metricContent[metric] || { title: 'Global Strategic Exposure', description: '' };
  };

  const currentMetricContent = getMetricContent(selectedMetric);

  // Group trade restriction policies by policy_title and country_name to consolidate same policies across years
  const groupedTradePolicies = useMemo(() => {
    if (!tradePolicyData || !tradePolicyData.trade_restriction_policies || tradePolicyData.trade_restriction_policies.length === 0) {
      return [];
    }
    
    const groups = {};
    tradePolicyData.trade_restriction_policies.forEach((policy) => {
      // Create a unique key from policy title and country
      const key = `${policy.policy_title || ''}_${policy.country_name || ''}`;
      if (!groups[key]) {
        groups[key] = {
          policy_title: policy.policy_title,
          country_name: policy.country_name,
          restriction_type: policy.restriction_type,
          policy_status: policy.policy_status,
          jurisdiction_level: policy.jurisdiction_level,
          mineral_name: policy.mineral_name,
          policy_source: policy.policy_source,
          reference_url: policy.reference_url,
          years: [],
          dates_enacted: []
        };
      }
      // Add year if not already present
      if (policy.year && !groups[key].years.includes(policy.year)) {
        groups[key].years.push(policy.year);
      }
      // Add date_enacted if not already present
      if (policy.date_enacted && !groups[key].dates_enacted.includes(policy.date_enacted)) {
        groups[key].dates_enacted.push(policy.date_enacted);
      }
    });
    // Sort years in descending order
    Object.values(groups).forEach(group => {
      group.years.sort((a, b) => b - a);
    });
    return Object.values(groups);
  }, [tradePolicyData]);

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white w-full transition-colors duration-200">
      <div className="w-full px-4 py-8">
        {/* Section 1: Supply and Market - Global Strategic Exposure */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white">{currentMetricContent.title}</h2>
            {currentMetricContent.description && (
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">
                {currentMetricContent.description}
              </p>
            )}
            
            {/* Main Layout - Left: Selection Criteria, Right: Visualization */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Side - Selection Criteria */}
              <div className="lg:col-span-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 space-y-4 transition-colors duration-200">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Selection Criteria</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Metric</label>
                <select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {metrics.map(metric => (
                    <option key={metric} value={metric}>{metric}</option>
                  ))}
                </select>
              </div>
              
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mineral</label>
                <select
                  value={selectedMineral}
                  onChange={(e) => setSelectedMineral(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {importRatioMinerals.map(mineral => (
                    <option key={mineral} value={mineral}>{mineral}</option>
                  ))}
                </select>
              </div>
              
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {(Array.isArray(availableYears) ? availableYears : ['2024', '2023', '2022', '2021', '2020']).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                  </div>
              </div>
            </div>

              {/* Right Side - Visualization Area */}
              <div className="lg:col-span-3">
                {/* View Toggle Buttons and Mineral Details - Outside the table container */}
                <div className="mb-4 flex justify-between items-center">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex shadow-lg transition-colors duration-200">
                    {viewOptions.map((view) => (
                      <button
                        key={view}
                        onClick={() => setSelectedView(view)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                          selectedView === view
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-gray-300 hover:text-white hover:bg-gray-600'
                        }`}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                  
                  {/* Mineral Details */}
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{selectedMineral}</span>
                    {selectedMineral !== 'All' && (
                      <span className="ml-1">- {selectedMetric}</span>
                    )}
                    <span className="ml-1">- {selectedYear}</span>
                  </div>
                </div>

                <div className="relative">

              {/* Map Projection Toggle - Only visible in Map view and not for Domestic Reserves */}
              {selectedView === 'Map' && selectedMetric !== 'Domestic Reserves' && (
                <div className="absolute top-4 right-4 z-10">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 shadow-lg transition-colors duration-200">
                    <div className="flex flex-col space-y-2">
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleProjectionChange('2D')}
                          disabled={isTransitioning}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                            mapProjection === '2D'
                              ? 'bg-green-600 text-white shadow-md'
                              : 'text-gray-300 hover:text-white hover:bg-gray-600'
                          } ${isTransitioning ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          2D
                        </button>
                        <button
                          onClick={() => handleProjectionChange('3D')}
                          disabled={isTransitioning}
                          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                            mapProjection === '3D'
                              ? 'bg-green-600 text-white shadow-md'
                              : 'text-gray-300 hover:text-white hover:bg-gray-600'
                          } ${isTransitioning ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          3D
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedView === 'Table' ? (
                // Table View
                <div className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 relative transition-colors duration-200"
                     style={{ minHeight: '500px' }}>
                  <div className="h-full overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-20">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 w-1/2">
                            {selectedMetric === 'Domestic Reserves' ? 'State Name' : 'Country Name (ISO)'}
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 w-1/2">
                            {selectedMetric === METRIC_IMPORT_DEPENDENCY ? 'Import Dependency %' :
                             selectedMetric === 'World Reserves' ? 'Reserve Data (tonnes)' :
                             selectedMetric === 'Trade Restrictions' ? 'Restriction Count' :
                             selectedMetric === 'Domestic Reserves' ? 'Reserve Quantity (tonnes)' :
                             selectedMetric}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const transformedData = getTransformedCountriesData();
                          
                          console.log('Table debug - transformedData:', transformedData.slice(0, 3));
                          console.log('Table debug - selectedMetric:', selectedMetric);
                          
                          if (transformedData.length > 0) {
                            let filteredData = [];
                            let sortField = '';
                            
                            // Filter and sort data based on selected metric
                            if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
                              filteredData = transformedData.filter(item => 
                                item.dependency_ratio !== null && 
                                item.dependency_ratio !== undefined && 
                                item.dependency_ratio > 0
                              );
                              sortField = 'dependency_ratio';
                            } else if (selectedMetric === 'World Reserves') {
                              // Check multiple possible field names for reserves data
                              filteredData = transformedData.filter(item => {
                                const hasReserves = (item.reserveValue && item.reserveValue > 0) ||
                                                   (item.world_reserves && item.world_reserves > 0) ||
                                                   (item.worldReserves && item.worldReserves > 0) ||
                                                   (item.reserves && item.reserves > 0) ||
                                                   (item.reserve_2024 && item.reserve_2024 > 0) ||
                                                   (item.reserve_2023 && item.reserve_2023 > 0);
                                
                                if (hasReserves) {
                                  console.log('Found country with reserves:', item.name, {
                                    reserveValue: item.reserveValue,
                                    world_reserves: item.world_reserves,
                                    worldReserves: item.worldReserves,
                                    reserves: item.reserves,
                                    reserve_2024: item.reserve_2024,
                                    reserve_2023: item.reserve_2023
                                  });
                                }
                                
                                return hasReserves;
                              });
                              sortField = 'reserveValue';
                            } else if (selectedMetric === 'Trade Restrictions') {
                              // Show all countries for trade restrictions, including those with 0 restrictions
                              filteredData = transformedData.filter(item => 
                                item.tradeRestrictions !== null && 
                                item.tradeRestrictions !== undefined
                              );
                              sortField = 'tradeRestrictions';
                            } else if (selectedMetric === 'Domestic Reserves') {
                              // For Domestic Reserves, don't show table - it uses India map component
                              filteredData = [];
                              sortField = 'name';
                            } else {
                              // Fallback to showing all data
                              filteredData = transformedData;
                              sortField = 'name';
                            }
                            
                            // Special handling for Domestic Reserves
                            if (selectedMetric === 'Domestic Reserves') {
                              // Show state-wise domestic reserves data
                              if (domesticReservesData && domesticReservesData.length > 0) {
                                return domesticReservesData
                                  .sort((a, b) => {
                                    const getQuantity = (reserve) => 
                                      reserve.reserve_quantity || 
                                      reserve.reserveQuantity || 
                                      reserve.quantity || 
                                      reserve.reserves || 
                                      reserve.amount || 
                                      reserve.value ||
                                      reserve.totalReserves ||
                                      reserve.reserveAmount || 0;
                                    
                                    return getQuantity(b) - getQuantity(a);
                                  })
                                  .map((reserve, index) => (
                                    <tr 
                                      key={`${reserve.state}-${reserve.mineral}-${index}`}
                                      className="hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 transition-colors duration-150"
                                    >
                                      <td className="px-4 py-3 text-gray-900 dark:text-gray-300 font-medium w-1/2">
                                        {reserve.state}
                                      </td>
                                      <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-300 w-1/2">
                                        {(() => {
                                          // Check multiple possible field names for reserve quantity
                                          const quantity = reserve.reserve_quantity || 
                                                          reserve.reserveQuantity || 
                                                          reserve.quantity || 
                                                          reserve.reserves || 
                                                          reserve.amount || 
                                                          reserve.value ||
                                                          reserve.totalReserves ||
                                                          reserve.reserveAmount;
                                          
                                          return quantity ? 
                                            `${quantity.toLocaleString()} tonnes` : 
                                            'N/A';
                                        })()}
                                      </td>
                                    </tr>
                                  ));
                              } else {
                                return (
                                  <tr>
                                    <td colSpan="2" className="px-4 py-8 text-center text-gray-600 dark:text-gray-400">
                                      <div className="text-lg mb-2">🏛️</div>
                                      <div>{loading ? 'Loading domestic reserves data...' : 'No domestic reserves data available'}</div>
                                      <div className="text-sm text-gray-600 dark:text-gray-500 mt-1">
                                        {!loading && 'Switch to Map view to see state-wise reserves'}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              }
                            }
                            
                            // If no filtered data, show all countries with available fields
                            if (filteredData.length === 0) {
                              console.log('No filtered data found, showing first 10 countries with available fields');
                              console.log('Sample country data:', transformedData[0]);
                              
                              
                              return transformedData.slice(0, 10).map((item, index) => (
                                <tr 
                                  key={`${item.name || item.country || index}-${index}`}
                                  className="hover:bg-gray-700 border-b border-gray-700 transition-colors duration-150"
                                >
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-300 font-medium w-1/2">
                                    {item.name || item.country || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-300 w-1/2">
                                    {(() => {
                                      if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
                                        return item.dependency_ratio !== null && item.dependency_ratio !== undefined 
                                          ? `${item.dependency_ratio.toFixed(2)}%` 
                                          : 'N/A';
                                      } else if (selectedMetric === 'World Reserves') {
                                        const reserveValue = item.reserveValue || item.world_reserves || item.worldReserves || item.reserves || item.reserve_2024 || item.reserve_2023;
                                        return reserveValue !== null && reserveValue !== undefined && reserveValue > 0
                                          ? `${reserveValue.toLocaleString()}` 
                                          : 'N/A';
                                      } else if (selectedMetric === 'Trade Restrictions') {
                                        return item.tradeRestrictions !== null && item.tradeRestrictions !== undefined 
                                          ? `${item.tradeRestrictions}` 
                                          : '0';
                                      } else if (selectedMetric === 'Domestic Reserves') {
                                        return item.reserve_quantity !== null && item.reserve_quantity !== undefined 
                                          ? `${item.reserve_quantity.toLocaleString()}` 
                                          : 'N/A';
                                      }
                                      return 'N/A';
                                    })()}
                                  </td>
                                </tr>
                              ));
                            }
                            
                            // Show filtered data sorted by the appropriate field
                            return filteredData
                              .sort((a, b) => {
                                if (sortField === 'name') return (a.name || '').localeCompare(b.name || '');
                                return (b[sortField] || 0) - (a[sortField] || 0);
                              })
                              .map((item, index) => (
                                <tr 
                                  key={`${item.name || item.country || index}-${index}`}
                                  className="hover:bg-gray-700 border-b border-gray-700 transition-colors duration-150"
                                >
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-300 font-medium w-1/2">
                                    {item.name || item.country || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-300 w-1/2">
                                    {(() => {
                                      if (selectedMetric === METRIC_IMPORT_DEPENDENCY) {
                                        return item.dependency_ratio !== null && item.dependency_ratio !== undefined 
                                          ? `${item.dependency_ratio.toFixed(2)}%` 
                                          : 'N/A';
                                      } else if (selectedMetric === 'World Reserves') {
                                        const reserveValue = item.reserveValue || item.world_reserves || item.worldReserves || item.reserves || item.reserve_2024 || item.reserve_2023;
                                        return reserveValue !== null && reserveValue !== undefined && reserveValue > 0
                                          ? `${reserveValue.toLocaleString()}` 
                                          : 'N/A';
                                      } else if (selectedMetric === 'Trade Restrictions') {
                                        return item.tradeRestrictions !== null && item.tradeRestrictions !== undefined 
                                          ? `${item.tradeRestrictions}` 
                                          : '0';
                                      } else if (selectedMetric === 'Domestic Reserves') {
                                        return item.reserve_quantity !== null && item.reserve_quantity !== undefined 
                                          ? `${item.reserve_quantity.toLocaleString()}` 
                                          : 'N/A';
                                      }
                                      return 'N/A';
                                    })()}
                                  </td>
                                </tr>
                              ));
                          } else {
                            return (
                              <tr>
                                <td colSpan="2" className="px-4 py-8 text-center text-gray-600 dark:text-gray-400">
                                  {loading ? 'Loading data...' : 'No data available'}
                                </td>
                              </tr>
                            );
                          }
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : selectedView === 'LineChart' ? (
                // Line Chart View
                <div className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 relative flex items-center justify-center transition-colors duration-200"
                     style={{ minHeight: '500px' }}>
                  <div className="text-center">
                    <div className="text-4xl mb-4">📈</div>
                    <div className="text-gray-800 dark:text-gray-300 text-lg">Line Chart View</div>
                    <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">Line chart visualization coming soon</div>
                  </div>
                </div>
              ) : selectedView === 'BarChart' ? (
                // Bar Chart View
                <div className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 relative flex items-center justify-center transition-colors duration-200"
                     style={{ minHeight: '500px' }}>
                  <div className="text-center">
                    <div className="text-4xl mb-4">📊</div>
                    <div className="text-gray-800 dark:text-gray-300 text-lg">Bar Chart View</div>
                    <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">Bar chart visualization coming soon</div>
                  </div>
                </div>
              ) : selectedView === 'Map' ? (
                // Map View (2D or 3D)
            <div className="relative">

                  {/* Transition Overlay */}
                  {isTransitioning && (
                    <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 bg-opacity-90 flex items-center justify-center z-20 rounded-lg transition-colors duration-200">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <div className="text-gray-300 text-lg">Switching to {mapProjection === '2D' ? '3D' : '2D'} view...</div>
                      </div>
                    </div>
                  )}

                  {(() => {
                    console.log('Rendering decision:', {
                      selectedMetric,
                      mapProjection,
                      selectedView,
                      isTransitioning
                    });
                    return null;
                  })()}
                  {mapProjection === '3D' ? (
                    // 3D Globe
                    <div 
                      key={`globe-${mapProjection}-${selectedMineral}-${selectedMetric}`}
                      ref={globeRef} 
                      className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-800 relative transition-colors duration-200"
                      style={{ minHeight: '500px' }}
                    >
                      <svg ref={globeSvgRef} className="w-full h-full"></svg>
                      {/* Force render 3D globe when component mounts */}
                      {(() => {
                        if (globeRef.current && globeSvgRef.current && !loading && mapData) {
                          setTimeout(() => {
                            console.log('Force rendering 3D globe from JSX');
                            renderGlobe();
                          }, 100);
                        }
                        return null;
                      })()}
                      {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 bg-opacity-90 transition-colors duration-200">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                            <div className="text-gray-800 dark:text-gray-300 text-lg">Loading 3D globe...</div>
                            <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">Fetching {selectedMineral} data for {selectedYear}</div>
                          </div>
                        </div>
                      )}
                      {!loading && (!mapData || !mapData.countries || mapData.countries.length === 0) && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 bg-opacity-90 transition-colors duration-200">
                          <div className="text-center">
                            <div className="text-4xl mb-4">🌍</div>
                            <div className="text-gray-800 dark:text-gray-300 text-lg">Globe data not available</div>
                            <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">No data available from API</div>
                            <div className="text-gray-600 dark:text-gray-500 text-xs mt-1">Mineral: {selectedMineral} | Year: {selectedYear} | Metric: {selectedMetric}</div>
                          </div>
                        </div>
                      )}
                      {/* Globe Instructions */}
                      <div className="absolute bottom-4 left-4 bg-gray-100 dark:bg-gray-900 bg-opacity-80 text-gray-900 dark:text-white text-xs px-3 py-2 rounded-lg transition-colors duration-200">
                        <div className="font-semibold mb-1">3D Globe Controls:</div>
                        <div>• Drag to rotate</div>
                        <div>• Click countries for details</div>
                      </div>
                </div>
              ) : (
                    // 2D World Map
                <div 
                      key={`map-${mapProjection}-${selectedMineral}-${selectedMetric}-${selectedView}`}
                  ref={mapRef} 
                  className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 relative transition-colors duration-200"
                  style={{ minHeight: '500px' }}
                >
                  <svg ref={svgRef} className="w-full h-full"></svg>
                  {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 bg-opacity-90 transition-colors duration-200">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <div className="text-gray-800 dark:text-gray-300 text-lg">Loading map data...</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">Fetching {selectedMineral} data for {selectedYear}</div>
                      </div>
                    </div>
                  )}
                  {!loading && (!mapData || !mapData.countries || mapData.countries.length === 0) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 bg-opacity-90 transition-colors duration-200">
                      <div className="text-center">
                        <div className="text-4xl mb-4">🗺️</div>
                        <div className="text-gray-800 dark:text-gray-300 text-lg">Map data not available</div>
                        <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">No data available from API</div>
                        <div className="text-gray-600 dark:text-gray-500 text-xs mt-1">Mineral: {selectedMineral} | Year: {selectedYear} | Metric: {selectedMetric}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* KPI - Bottom Left Corner */}
                  {selectedMetric === METRIC_IMPORT_DEPENDENCY && calculateImportRatioKPIs() && (
                    <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-600 transition-colors duration-200 min-w-[200px]">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Import Dependency KPI
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600 dark:text-gray-400">World Value:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {typeof calculateImportRatioKPIs().worldValue === 'number' 
                              ? `$${(calculateImportRatioKPIs().worldValue / 1000000).toFixed(0)}M`
                              : calculateImportRatioKPIs().worldValue
                            }
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Top Importer:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {calculateImportRatioKPIs().highestImportingCountry}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          {selectedMineral} • {selectedYear}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* World Reserves KPI - Bottom Left Corner */}
                  {selectedMetric === 'World Reserves' && calculateWorldReservesKPIs() && (
                    <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-600 transition-colors duration-200 min-w-[200px]">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        World Reserves KPI
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Total Reserves:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {typeof calculateWorldReservesKPIs().worldValue === 'number' 
                              ? `${(calculateWorldReservesKPIs().worldValue / 1000000).toFixed(0)}M tonnes`
                              : calculateWorldReservesKPIs().worldValue
                            }
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Top Reserves:</span>
                          <span className="text-xs font-medium text-gray-900 dark:text-white">
                            {calculateWorldReservesKPIs().highestReservesCountry}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                          {selectedMineral} • {selectedYear}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
                </div>
              ) : null}
                </div>
              </div>
            </div>

            {/* Country Detail Panel - Fixed Dark Theme Design */}
            {selectedCountry && countryDetail && (
              <div className="fixed bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-h-[400px] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-600 z-50 transition-colors duration-200"
                   style={{
                     ...(() => {
                       const modalWidth = 384; // w-96 = 24rem = 384px
                       const modalHeight = 400; // max-h-[400px]
                       const padding = 20;
                       
                       // Get the appropriate container based on current view
                       let mapContainer;
                       if (selectedMetric === 'Domestic Reserves') {
                         // For Domestic Reserves, use the India map container
                         mapContainer = document.querySelector('.india-map-container') || mapRef.current;
                       } else {
                         mapContainer = mapProjection === '3D' ? globeRef.current : mapRef.current;
                       }
                       let left, top;
                       
                       if (selectedCountry.clickX && selectedCountry.clickY) {
                         const offset = 20; // Offset from click position
                         
                         console.log('Modal positioning debug:', {
                           clickX: selectedCountry.clickX,
                           clickY: selectedCountry.clickY,
                           clickXRelative: selectedCountry.clickXRelative,
                           clickYRelative: selectedCountry.clickYRelative,
                           containerWidth: mapContainer?.clientWidth,
                           containerHeight: mapContainer?.clientHeight,
                           modalWidth,
                           modalHeight
                         });
                         
                         // Position relative to the viewport (fixed positioning)
                         left = selectedCountry.clickX + offset;
                         top = selectedCountry.clickY - offset;
                         
                         // If modal would go off the right edge, position it to the left of click
                         if (left + modalWidth > window.innerWidth - padding) {
                           left = selectedCountry.clickX - modalWidth - offset;
                         }
                         
                         // If modal would go off the bottom edge, position it above click
                         if (top + modalHeight > window.innerHeight - padding) {
                           top = selectedCountry.clickY - modalHeight - offset;
                         }
                         
                         // Ensure modal stays within viewport bounds
                         if (left < padding) left = padding;
                         if (top < padding) top = padding;
                         
                         console.log('Final modal position:', { left, top });
                       } else {
                         // Fallback to center positioning
                         left = 50;
                         top = 50;
                         console.log('Using fallback positioning:', { left, top });
                       }
                       
                       return {
                         left: `${left}px`,
                         top: `${top}px`
                       };
                     })()
                   }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {countryDetail.countryInfo?.name || selectedCountry.properties.name || selectedCountry.properties.NAME || selectedCountry.properties.ADMIN || 'Unknown'}
                    {selectedMetric === 'Trade Restrictions' && (
                      <span className="text-lg font-normal text-gray-700 dark:text-gray-300 ml-2">
                        - {selectedMineral}
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={closeCountryDetail}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-xl transition-colors duration-200"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* World Reserves specific modal content */}
                  {countryDetail.isWorldReserves ? (
                    <div className="space-y-4">
                      {/* Indicators Section */}
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Indicators</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Year</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{countryDetail.countryInfo?.year || '2024'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Mineral Production Section */}
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Mineral Production</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Production Data</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{countryDetail.countryInfo?.productionData || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Reserve Section */}
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Reserve</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Reserve Data</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{countryDetail.countryInfo?.reserveData || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Data Source */}
                      <div className="text-center pt-4 border-t border-gray-600">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Data Source: {countryDetail.countryInfo?.dataSource || 'MCS2025'}</span>
                      </div>
                    </div>
                  ) : countryDetail.isTradeRestrictions ? (
                    <div className="space-y-4">
                      {/* Trade Restriction Section */}
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Trade Restriction</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Restriction Type</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {(() => {
                                console.log('=== MODAL RENDER DEBUG ===');
                                console.log('countryDetail:', countryDetail);
                                console.log('restrictionTypes:', countryDetail.restrictionTypes);
                                console.log('primary_restriction:', countryDetail.primary_restriction);
                                
                                // Use primary_restriction if available
                                if (countryDetail.primary_restriction) {
                                  console.log('Displaying primary restriction:', countryDetail.primary_restriction);
                                  return countryDetail.primary_restriction;
                                }
                                
                                // Fallback to restrictionTypes array
                                if (countryDetail.restrictionTypes && countryDetail.restrictionTypes.length > 0) {
                                  const restrictionType = countryDetail.restrictionTypes[0].type || countryDetail.restrictionTypes[0];
                                  console.log('Displaying restriction type from array:', restrictionType);
                                  return restrictionType;
                                } else {
                                  console.log('No restrictions found, displaying default');
                                  return 'No restrictions';
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Indicators Section */}
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 transition-colors duration-200">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Indicators</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600 dark:text-gray-400">Year</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">{selectedYear}</span>
                          </div>
                        </div>
                      </div>

                      {/* Data Source */}
                      <div className="text-center pt-4 border-t border-gray-600">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Data Source: Trade Restrictions Database</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Dependency info for non-Partner Risk metrics */}
                      {selectedMetric !== 'Partner Risk' && (
                        <div className="bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4 transition-colors duration-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xl font-bold text-gray-700 dark:text-gray-300">{selectedMetric}</span>
                            </div>
                            <span className="text-xl font-bold text-white-600 dark:text-white-400">
                              {countryDetail.dependencyRatio}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Recent Alerts and KABIL sections - only show for non-World Reserves and non-Trade Restrictions metrics */}
                  {!countryDetail.isWorldReserves && !countryDetail.isTradeRestrictions && (
                    <>
                      {/* Recent Alerts by PESTLE Category */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Recent Alerts</h4>
                        {countryDetail.countryAlerts && countryDetail.countryAlerts.length > 0 ? (
                          <div className="space-y-2">
                            {countryDetail.countryAlerts.slice(0, 3).map((alert, index) => (
                              <div key={index} className="flex items-start gap-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg transition-colors duration-200">
                                <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                                  getPestleRiskLevelDotColor(alert.pestle_risk_level || alert.severity)
                                }`}></div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{alert.date}</div>
                                  <div className="text-sm text-gray-900 dark:text-white font-medium mb-1">
                                    {alert.pestleCategory || 'General'}
                                  </div>
                                  <div className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                                    {alert.message}
                                  </div>
                                  <div className="flex items-center gap-2 mt-2">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      getPestleRiskLevelColor(alert.pestle_risk_level || alert.severity).replace('text-white', 'text-red-200').replace('bg-red-600', 'bg-red-800').replace('bg-red-500', 'bg-red-900').replace('bg-yellow-500', 'bg-yellow-900').replace('bg-green-500', 'bg-green-900')
                                    }`}>
                                      {alert.pestle_risk_level || alert.severity}
                                    </span>
                                    {alert.pestleCategory && (
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        alert.pestleCategory === 'Economic' ? 'bg-yellow-800 text-yellow-100' :
                                        alert.pestleCategory === 'Environmental' ? 'bg-green-800 text-green-100' :
                                        alert.pestleCategory === 'Political' ? 'bg-red-800 text-red-100' :
                                        alert.pestleCategory === 'Social' ? 'bg-blue-800 text-blue-100' :
                                        alert.pestleCategory === 'Technological' ? 'bg-purple-800 text-purple-100' :
                                        alert.pestleCategory === 'Legal' ? 'bg-orange-800 text-orange-100' : 'bg-gray-800 text-gray-100'
                                      }`}>
                                        {alert.pestleCategory}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {countryDetail.countryAlerts.length > 3 && (
                              <div className="text-center py-2">
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  +{countryDetail.countryAlerts.length - 3} more alerts
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-600 dark:text-gray-400">
                            <div className="text-sm text-gray-800 dark:text-gray-300">No alerts available</div>
                            <div className="text-xs mt-1 text-gray-600 dark:text-gray-400">Alerts will appear as articles are analyzed</div>
                          </div>
                        )}
                      </div>
                      
                      {/* KABIL Agreement Summary */}
                      <div className="pt-3 border-t border-gray-200 dark:border-gray-600">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-2">KABIL Agreement Summary</h4>
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 transition-colors duration-200">
                          <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                            <span className="font-medium">Status:</span> {countryDetail.kabilAgreement?.status}
                          </div>
                          {countryDetail.kabilAgreement?.stage && countryDetail.kabilAgreement.stage !== 'Not applicable' && (
                            <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                              <span className="font-medium">Stage:</span> {countryDetail.kabilAgreement.stage}
                            </div>
                          )}
                          <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                            <span className="font-medium">Type:</span> {countryDetail.kabilAgreement?.type}
                          </div>
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            <span className="font-medium">Details:</span> {countryDetail.kabilAgreement?.details}
                          </div>
                          {countryDetail.kabilAgreement?.partner && (
                            <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                              <span className="font-medium">Partner:</span> {countryDetail.kabilAgreement.partner}
                            </div>
                          )}
                          {countryDetail.kabilAgreement?.minerals && countryDetail.kabilAgreement.minerals.length > 0 && (
                            <div className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                              <span className="font-medium">Minerals:</span> {countryDetail.kabilAgreement.minerals.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Domestic Reserves Section */}
        

        {/* Trade Policy Tracker Section removed */}

        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">India's Resource Distribution</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This chart maps the domestic availability of critical minerals within the country. It highlights areas with resource potential and supports planning for exploration, extraction, and capacity development. </p>
            
            {/* KPI Summary Cards */}
            {calculateDomesticReservesKPIs && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                
                {/* KPI 1: Top Reserve State */}
                <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                  <div className="mb-3">
                    <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Top Reserve State</div>
                  </div>
                  
                  <div className="kpi-value">
                    {calculateDomesticReservesKPIs.topState.name}
                  </div>
                  
                  <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                    {calculateDomesticReservesKPIs.topState.percentage}% share
                  </div>
                  
                  <div className="text-gray-600 dark:text-gray-400 text-sm mt-2">
                    {calculateDomesticReservesKPIs.topState.tonnage.toLocaleString()} tonnes
                  </div>
                </div>

                {/* KPI 2: Most Concentrated Mineral */}
                <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                  <div className="mb-3">
                    <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>
                      Most Concentrated
                    </div>
                  </div>
                  
                  <div className="kpi-value">
                    {calculateDomesticReservesKPIs.mostConcentrated.mineral}
                  </div>
                  
                  <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                    {calculateDomesticReservesKPIs.mostConcentrated.concentration}% concentration
                  </div>
                  
                  {/* Detailed breakdown */}
                  <div className="kpi-divider" style={{ borderTopColor: 'var(--kpi-divider)' }}>
                    <div className="space-y-1.5">
                      {calculateDomesticReservesKPIs.mostConcentrated.breakdown.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-gray-700 dark:text-gray-300">{item.state}</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{item.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* KPI 3: Total Identified Reserves */}
                <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                  <div className="mb-3">
                    <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>
                      Total Reserves
                    </div>
                  </div>
                  
                  <div className="kpi-value">
                    {calculateDomesticReservesKPIs.totalReserves.tonnage.toLocaleString()} tonnes 
                  </div>
                  
                  <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                    {calculateDomesticReservesKPIs.totalReserves.mineralsCount} minerals
                  </div>
                  
                  {/* All Minerals Breakdown */}
                  <div className="kpi-divider" style={{ borderTopColor: 'var(--kpi-divider)' }}>
                    <div className="space-y-1.5">
                      {calculateDomesticReservesKPIs.totalReserves.topMinerals.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-gray-700 dark:text-gray-300">{item.mineral}</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{item.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Main Layout - Left: Selection Criteria, Right: Visualization */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Side - Selection Criteria */}
              <div className="lg:col-span-1">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 space-y-4 transition-colors duration-200">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Selection Criteria</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mineral</label>
                    <select
                      value={domesticReservesMineral}
                      onChange={(e) => setDomesticReservesMineral(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      {minerals.map(mineral => (
                        <option key={mineral} value={mineral}>{mineral}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type</label>
                    <select
                      value={domesticReservesType}
                      onChange={(e) => setDomesticReservesType(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      {domesticReservesTypeOptions.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Right Side - Visualization */}
              <div className="lg:col-span-3">
                {/* View Toggle Buttons and Mineral Details - Outside the table container */}
                <div className="mb-4 flex justify-between items-center">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-1 flex shadow-lg transition-colors duration-200">
                    {domesticReservesViewOptions.map((view) => (
                      <button
                        key={view}
                        onClick={() => setDomesticReservesView(view)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                          domesticReservesView === view
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'text-gray-300 hover:text-white hover:bg-gray-600'
                        }`}
                      >
                        {view}
                      </button>
                    ))}
                  </div>
                  
                  {/* Mineral Details */}
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">{domesticReservesMineral}</span>
                    <span className="ml-1">- {domesticReservesType}</span>
                  </div>
                </div>

                <div className="relative">

                {domesticReservesView === 'Table' ? (
                  // Table View
                  <div className="w-full h-96 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 relative transition-colors duration-200"
                       style={{ minHeight: '500px' }}>
                    <div className="h-full overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-20">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 w-1/3">State Name</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 w-1/3">Mineral</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-200 border-b border-gray-200 dark:border-gray-600 w-1/3">
                              {domesticReservesType === 'Reserves' ? 'Reserve Quantity (tonnes)' : 'Production (tonnes)'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {domesticReservesData && domesticReservesData.length > 0 ? (
                            domesticReservesData
                              .sort((a, b) => {
                                const getQuantity = (item) => 
                                  item.reserve_quantity || 
                                  item.reserveQuantity || 
                                  item.quantity || 
                                  item.reserves || 
                                  item.amount || 
                                  item.value ||
                                  item.totalReserves ||
                                  item.reserveAmount || 0;
                                
                                return getQuantity(b) - getQuantity(a);
                              })
                              .map((item, index) => (
                                <tr 
                                  key={`${item.state}-${item.mineral}-${index}`}
                                  className="hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 transition-colors duration-150"
                                >
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-300 font-medium">
                                    {item.state || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-gray-900 dark:text-gray-300">
                                    {item.mineral || 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-300">
                                    {(() => {
                                      const quantity = item.reserve_quantity || 
                                                      item.reserveQuantity || 
                                                      item.quantity || 
                                                      item.reserves || 
                                                      item.amount || 
                                                      item.value ||
                                                      item.totalReserves ||
                                                      item.reserveAmount;
                                      
                                      return quantity !== null && quantity !== undefined 
                                        ? `${quantity.toLocaleString()}` 
                                        : 'N/A';
                                    })()}
                                  </td>
                                </tr>
                              ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                No data available for {domesticReservesMineral}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  // Map View
                  <div className="w-full border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-100 relative overflow-auto india-map-container transition-colors duration-200"
                       style={{ minHeight: '500px' }}>
                    <IndiaDomesticReservesMap selectedMineral={domesticReservesMineral} type={domesticReservesType} />
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Trade Time Series */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Global Trade Trends of Critical Minerals</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">This visualization tracks overall trade trends and fluctuations across critical minerals. 
            It provides perspective on market cycles, volatility, and longer-term trade stability, supporting informed procurement and investment decisions. </p>
            {/* Time Series Controls */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mineral:</label>
                <select
                  value={timeSeriesMineral}
                  onChange={(e) => setTimeSeriesMineral(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {availableTimeSeriesMinerals.map(mineral => (
                    <option key={mineral} value={mineral}>{mineral}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Flow:</label>
                <select
                  value={timeSeriesFlow}
                  onChange={(e) => setTimeSeriesFlow(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {availableTimeSeriesFlows.map(flow => (
                    <option key={flow} value={flow}>{flow}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Year:</label>
                <select
                  value={timeSeriesYear}
                  onChange={(e) => setTimeSeriesYear(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <option value="All">All Years</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                  <option value="2021">2021</option>
                  <option value="2020">2020</option>
                </select>
              </div>
            </div>

            {/* Time Series Charts */}
            <TimeSeriesCharts 
              data={filteredTimeSeriesData}
              loading={timeSeriesLoading}
              selectedMineral={timeSeriesMineral}
              selectedFlow={timeSeriesFlow}
              selectedYear={timeSeriesYear}
            />

          </div>
        </section>

        {/* Section 3: Prices Time Series */}
        <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Global Price Trends of Critical Minerals</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-xm leading-relaxed">A high-impact intelligence snapshot that highlights the most volatile minerals, biggest price movers, and key market shifts. Ideal for rapid risk assessment and procurement planning. </p>
            
            {/* Pricing KPIs Cards */}
            {pricingKPIs && pricingKPIs.success && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Top Gainer - only show if there are positive gainers */}
                {pricingKPIs.data.top_gainer && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Top Gainer (YoY)</div>
                    </div>
                    
                    <div className="kpi-value">
                      {pricingKPIs.data.top_gainer.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-green)' }}>
                      +{pricingKPIs.data.top_gainer.yoy_growth}%
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Highest growth in 2024
                    </div>
                  </div>
                )}

                {/* No Top Gainer message when all minerals are declining */}
                {!pricingKPIs.data.top_gainer && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Top Gainer (YoY)</div>
                    </div>
                    
                    <div className="kpi-value">
                      No Positive Growth
                    </div>
                    
                    <div className="text-gray-500 dark:text-gray-500 text-base font-semibold">
                      N/A
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      All minerals declined in 2024
                    </div>
                  </div>
                )}

                {/* Biggest Decline */}
                {pricingKPIs.data.biggest_decline && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Biggest Decline</div>
                    </div>
                    
                    <div className="kpi-value">
                      {pricingKPIs.data.biggest_decline.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--color-accent-red)' }}>
                      {pricingKPIs.data.biggest_decline.yoy_change}%
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Steepest price correction
                    </div>
                  </div>
                )}

                {/* Volatility Leader */}
                {pricingKPIs.data.volatility_leader && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Volatility Leader</div>
                    </div>
                    
                    <div className="kpi-value">
                      {pricingKPIs.data.volatility_leader.mineral}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                      σ = {pricingKPIs.data.volatility_leader.std_dev}
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      Highest 5-year volatility
                    </div>
                  </div>
                )}

                {/* Month of Sharpest Movement */}
                {pricingKPIs.data.month_of_sharpest_movement && (
                  <div className="kpi-card" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="mb-3">
                      <div className="kpi-label" style={{ color: 'var(--kpi-text)' }}>Sharpest Movement</div>
                    </div>
                    
                    <div className="kpi-value">
                      {pricingKPIs.data.month_of_sharpest_movement.month_name}
                    </div>
                    
                    <div className="kpi-metric" style={{ color: 'var(--kpi-text)' }}>
                      +{pricingKPIs.data.month_of_sharpest_movement.avg_movement}%
                    </div>
                    
                    <div className="text-gray-600 dark:text-gray-400 text-xs mt-2">
                      {pricingKPIs.data.month_of_sharpest_movement.top_movers.map(m => m.mineral).join(', ')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Loading state for KPIs */}
            {kpiLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="kpi-card animate-pulse" style={{ borderColor: 'var(--kpi-border)' }}>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded mb-1"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Pricing Time Series Controls */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Mineral:</label>
                <select
                  value={pricingMineral}
                  onChange={(e) => setPricingMineral(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {availablePricingMinerals.map(mineral => (
                    <option key={mineral} value={mineral}>{mineral}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Year:</label>
                <select
                  value={pricingYear}
                  onChange={(e) => setPricingYear(e.target.value)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {availablePricingYears.map(year => (
                    <option key={year} value={year}>
                      {year === 'All' ? 'All Years' : year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pricing Time Series Charts */}
            <PricingTimeSeriesCharts 
              data={filteredPricingData}
              loading={pricingLoading}
              selectedMineral={pricingMineral}
              selectedYear={pricingYear}
            />
          </div>
        </section>
        {/* Section 4: Mineral → Sector Treemap - Reason Section has been hidden due to unavailability of Data Sources*/}
        {/* <section className="mb-8">
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-600 transition-colors duration-200">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Mineral → Sector Treemap</h2>
            <MineralSectorTreeMap />
          </div>
        </section> */}
      </div>
    </div>
  );
};

export default SupplyMarketsPage;
